import { Stack } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import { Box, Divider, IconButton, LinearProgress, styled, Typography, Zoom } from "@mui/material";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Joyride, { STATUS, Step } from "react-joyride";
import { Mention, MentionsInput } from "react-mentions";
import { useDispatch, useSelector } from "react-redux";
import { useSetState } from "../../hooks/useSetState";
import { AppType, ChatAppResponse, getAppTypeBasedOnLastAnswerIsRelatedToAriel, QuestionDetails, saveUserSettings, UploadFile } from "../../api";
import type { AllProductPrecheckCandidate, AllProductPrecheckSelectionDecision } from "../../api";
import { resolvePrecheckAutoSelectedProduct } from "../../utils/precheckSelection";
import { appInfo, allProductAutoSelectStaticDefaults, userInfo } from "../../authConfig";
import i18next from "../../i18n/i18n";
import store, {
    RootState,
    selectTemplateContents,
    selectTemplateTitles,
    selectUserDefaultContent,
    selectSelectedProduct,
    selectTemplateAppliedFlag,
    selectRequestedToForceStop,
    selectAnswerGenerationActive,
    selectCurrentSessionId,
    selectFileAttachments,
    selectFileUploadOrDeleteAPITriggerActive,
    selectActiveFileAttachmentTour,
    selectSuggestionsPendingAction,
    selectSuggestionsStage
} from "../../Store";
import { AllProductSuggestionsAction } from "../../Store/AllProductSuggestionsReducer";
import type { AllProductCitationInsight, AllProductScoreInsight, SuggestionsLoadingPhase } from "../../Store/AllProductSuggestionsReducer";
import { AnswerGenerationActiveDetails, FileAttachmentDetails, QuestionInputReducerAction } from "../../Store/QuestionInputReducer";
import { setSaveTemplateIdInStore } from "../../Store/TemplateContents";
import { FileAttachmentButton } from "../FileAttachments/FileAttachmentButton";
import { FileAttachmentPreviewRow } from "../FileAttachments/FileAttachmentPreviewRow";
import { Attachment, FileAttachments, TriggerTypeAPI } from "../FileAttachments/FileAttachments";
import { isFileTypeAccepted, resolveAcceptedUploadFileTypes, EXTENSION_TO_MIME_MAP } from "../FileAttachments/fileUtils";
import {
    getSystemDefaultTemplate as getSharedSystemDefaultTemplate,
    getTemplatePromptTextValue,
    getUserDefaultContentTemplate as getSharedUserDefaultContentTemplate
} from "../../pages/chat/utils/templateHelpers";
import ChatAPIResponseGenerationAbortButton from "./ChatAPIResponseGenerationAbortButton";
import MentionStyle from "./MentionStyle";
import styles from "./QuestionInput.module.css";
import FileUploadOrDeleteProgressBar from "../FileAttachments/FileUploadOrDeleteProgressBar";
type MentionsInputStyle = React.ComponentProps<typeof MentionsInput>["style"];

interface Props {
    mode?: "chat" | "history";
    onSend: (question: string, questionContainsTemplateValue: boolean, appType: AppType) => void;
    disabled: boolean;
    clearOnSend?: boolean;
    templateContent?: any;
    selectedTemplateContentId?: string;
    onClearTemplateContent?: () => void;
    disableTextarea: boolean;
    productIndex: { manual_key: string; faq_key: string };
    isAdmin: boolean;
    isEdited: boolean;
    setIsEdited: (isEdited: boolean) => void;
    clearChatTriggered?: boolean | undefined;
    setClearChatTriggered?: (isEdited: boolean) => void;
    chatContentCleared?: boolean | undefined;
    setChatContentCleared?: (chatContentCleared: boolean) => void;
    selectedAppType: string;
    changingTemplateContent?: boolean;
    setChangingTemplateContent?: (changingTemplateContentFlag: boolean) => void;
    isCitationActive: boolean;
    isHistoryPageInitialLoading?: boolean;
    setIsHistoryPageInitialLoading?: (isHistoryPageInitialLoadingFlag: boolean) => void;
    handleTemplateContentsBySuggestions?: (contents: string, tempId: string) => void;
    isNewChatThreadInitialLoading?: boolean;
    setIsNewChatThreadInitialLoading?: (isNewChatThreadInitialLoadingFlag: boolean) => void;
    answers?: {}[];
    setSelectedAppType?: (appType: AppType) => void;
    setActiveFileAttachmentTour?: (tourFlag: boolean) => void;
    onAllProductPrecheck?: (
        sendRequest: PreparedSendRequest,
        onProgress?: (update: AllProductPrecheckProgressUpdate) => void
    ) => Promise<AllProductPrecheckResult>;
    onAbortAllProductPrecheck?: () => void;
    onAllProductTagSelected?: (
        selectedProduct: AllProductMatchedProduct,
        sendRequest: PreparedSendRequest,
        selectionMeta?: AllProductSelectionMeta
    ) => Promise<void>;
    allProductAutoSelectConfig?: AllProductAutoSelectRuntimeConfig;
    showInlineAllProductValidationFallback?: boolean;
    clarificationBanner?: { type: "greeting" | "vague" | "off_topic"; message: string } | null;
    onSendAnyway?: () => void;
    onDismissClarification?: (skipValidation?: boolean, softDismiss?: boolean) => void;
}

export interface QuestionInputRef {
    clearTemplateContentFromQuestionInput: () => void;
    clearAfterExternalSend: () => void;
    getCurrentQuestionInputValue: () => string;
    onForceStopAIResponseGenerationRetainQuestion: (answerGenerationActiveDetails: AnswerGenerationActiveDetails) => void;
    setQuestionInputValue: (value: string) => void;
    /** Re-send with the given question text going through the full ALL-product precheck flow. */
    triggerSendWithQuestion: (question: string) => void;
}

interface TourState {
    run: boolean;
    steps: Step[];
}

type AllProductValidationStage = "idle" | "loading" | "confirm" | "select";

export interface PreparedSendRequest {
    rawQuestionText: string;
    questionText: string;
    questionContainsTemplateValue: boolean;
    appTypeBasedOnUser: AppType;
    sourceMode?: "chat" | "history";
}

export interface AllProductPrecheckProgressUpdate {
    phase: "search" | "score";
    matchedProducts: AllProductMatchedProduct[];
    searchCitations?: AllProductCitationInsight[];
    scoreCandidates?: AllProductScoreInsight[];
}

export interface AllProductPrecheckResult {
    matchedProducts: AllProductMatchedProduct[];
    selectionDecision?: AllProductPrecheckSelectionDecision | null;
    /** Present when the backend rejected the question as vague/greeting/off_topic */
    questionValidation?: { is_vague: boolean; type: "greeting" | "vague" | "off_topic"; message: string } | null;
}

const normalizeAllProductPrecheckResult = (result: AllProductPrecheckResult | AllProductMatchedProduct[] | null | undefined): AllProductPrecheckResult => {
    if (Array.isArray(result)) {
        return {
            matchedProducts: result,
            selectionDecision: null,
            questionValidation: null
        };
    }

    return {
        matchedProducts: Array.isArray(result?.matchedProducts) ? result.matchedProducts : [],
        selectionDecision: result?.selectionDecision ?? null,
        questionValidation: result?.questionValidation ?? null
    };
};

export interface AllProductMatchedProduct {
    productText: string;
    manual_key: string;
    faq_key: string;
    count: number;
    confidence?: number;
    relevanceScore?: number;
    averageScore?: number;
    maxScore?: number;
    scoreSampleCount?: number;
    rerankerScores?: number[];
    displayText?: string;
    fromBrowse?: boolean;
    productVersion?: {
        manual_version_key: string;
        faq_version_key: string;
    };
    subProduct?: {
        manual_sub_key: string;
        faq_sub_key: string;
    };
}

export interface AllProductAutoSelectRuntimeConfig {
    enabled: boolean;
    minConfidence: number;
    minMargin: number;
    minHits: number;
    minAvgRerankerScore?: number;
    minAvgRerankerGap?: number;
    weightAverage?: number;
    weightPeak?: number;
    weightVolume?: number;
}

export interface AllProductAutoSelectionSnapshot {
    selectedProductLabel: string;
    // The model/heuristic's raw pick before any customer PTF-config override (same as
    // selectedProductLabel when customerConfigApplied is false).
    modelSelectedProductLabel?: string | null;
    // True when the customer's registered PTF product/version config overrode the model's pick.
    customerConfigApplied?: boolean | null;
    // Score/relevance for the model's raw pick vs. the PTF-effective pick — same values
    // when customerConfigApplied is false.
    modelSelectedScore?: number | null;
    modelSelectedCitationCount?: number | null;
    ptfSelectedScore?: number | null;
    ptfSelectedCitationCount?: number | null;
    ptfSelectedProductIndexes?: string[] | null;
    ptfSelectedVersionIndexes?: string[] | null;
    ptfSelectedSubProductIndexes?: string[] | null;
    precheckCandidates?: AllProductPrecheckCandidate[] | null;
    // True when the corp has a PTF registration on file for this product at all.
    customerConfigRegistered?: boolean | null;
    // The customer's registered version major, and the major actually used, for display
    // when the registered version isn't among the candidates (customerConfigComparison).
    customerConfigVersionMajor?: string | null;
    customerConfigEffectiveVersionMajor?: string | null;
    // "matched" | "config_lower" | "config_higher" | "unknown" | "not_registered" | "registered_no_version" | "lookup_skipped" | "lookup_failed"
    customerConfigComparison?: string | null;
    decisionSource?: "server_model" | "ui_fallback";
    decisionReason?: string | null;
    decisionReasonCode?: string | null;
    selectedConfidence: number | null;
    selectedHits: number;
    secondProductLabel: string | null;
    secondConfidence: number | null;
    confidenceMargin: number | null;
    candidateCount: number;
    citationCount: number;
    minConfidence: number;
    minHits: number;
    minMargin: number;
    selectionMethod?: "reranker" | "confidence";
    selectedAverageRerankerScore?: number | null;
    secondAverageRerankerScore?: number | null;
    selectedRerankerQualifiedCount?: number | null;
    secondRerankerQualifiedCount?: number | null;
    rerankerGap?: number | null;
    minAvgRerankerScore?: number;
    minAvgRerankerGap?: number;
}

export interface AllProductSelectionMeta {
    source: "auto" | "manual" | "continue_all";
    autoSelectionSnapshot?: AllProductAutoSelectionSnapshot | null;
}

interface ProductSelectionSnapshot {
    selectedProduct: string;
    manualKey: string;
    faqKey: string;
}

const normalizeSessionIdentity = (value: unknown): string => String(value ?? "").trim();

export const QuestionInput = React.memo(
    forwardRef<QuestionInputRef, Props>(
        (
            {
                mode = "chat",
                onSend,
                disabled,
                clearOnSend,
                templateContent,
                selectedTemplateContentId,
                onClearTemplateContent,
                disableTextarea,
                productIndex,
                isAdmin,
                isEdited,
                setIsEdited,
                clearChatTriggered,
                setClearChatTriggered,
                chatContentCleared,
                setChatContentCleared,
                selectedAppType,
                changingTemplateContent,
                setChangingTemplateContent,
                isCitationActive,
                isHistoryPageInitialLoading,
                setIsHistoryPageInitialLoading,
                handleTemplateContentsBySuggestions,
                isNewChatThreadInitialLoading,
                setIsNewChatThreadInitialLoading,
                answers,
                setSelectedAppType,
                setActiveFileAttachmentTour,
                onAllProductPrecheck,
                onAbortAllProductPrecheck,
                onAllProductTagSelected,
                allProductAutoSelectConfig,
                showInlineAllProductValidationFallback = true,
                clarificationBanner,
                onSendAnyway,
                onDismissClarification
            }: Props,
            ref
        ) => {
            const [question, setQuestion] = useState<string>("");

            const templateContents = useSelector(selectTemplateContents);
            const templateTitles = useSelector(selectTemplateTitles);
            const userDefaultContent = useSelector(selectUserDefaultContent);
            const selectedProduct = useSelector(selectSelectedProduct);
            const mentionsInputRef = useRef<HTMLTextAreaElement | null>(null);
            const previousSelectedProductRef = useRef<string | null>(null);
            const [questionContainsTemplate, setQuestionContainsTemplate] = useState<boolean>(false);
            const [sendQuestionDisabled, setSendQuestionDisabled] = useState<boolean>(true);
            const [difference, setDifference] = useState("");

            const title: any = templateContents.find((content: any) => content.product);

            const allOptionsUserDefault: any = getSharedUserDefaultContentTemplate("ALL", userInfo?.["userFavourite"], templateContents);

            const dispatch = useDispatch();
            const templateAppliedFlagValue = useSelector(selectTemplateAppliedFlag);
            // Redux state for undoStack and redoStack
            const undoStack: Array<string> = useSelector((state: RootState) => state.questionInput.questionInputUndoHistoryStack);
            const redoStack: Array<string> = useSelector((state: RootState) => state.questionInput.questionInputRedoHistoryStack);
            const focusPositionToRetain: number | null = useSelector((state: RootState) => state.questionInput.focusPositionToRetain);

            const requestedToForceStopAIResponseGeneration = useSelector(selectRequestedToForceStop);
            const answerGenerationActive = useSelector(selectAnswerGenerationActive);
            const currentSessionId = useSelector(selectCurrentSessionId);
            const normalizedCurrentSessionId = normalizeSessionIdentity(currentSessionId);
            const [defaultContentState, setDefaultContentState] = useState<string>(!requestedToForceStopAIResponseGeneration ? (userDefaultContent ?? "") : "");
            const fileInputRef = useRef<HTMLInputElement>(null);
            const { addFiles, removeAttachment, SnackbarComponent, showSnackbar } = FileAttachments();
            const fileAttachments: Array<Attachment> = useSelector(selectFileAttachments);
            const fileUploadOrDeleteAPITriggerActive = useSelector(selectFileUploadOrDeleteAPITriggerActive);
            const activeFileAttachmentTour = useSelector(selectActiveFileAttachmentTour);
            const getCurrentQuestionInputValue = () => defaultContentState || (isEdited ? question : getTemplatePromptTextValue(templateContent)) || "";
            const showAbortButton = answerGenerationActive && !requestedToForceStopAIResponseGeneration;
            const [allProductValidationStage, setAllProductValidationStage] = useState<AllProductValidationStage>("idle");
            const [isQuestionValidating, setIsQuestionValidating] = useState<boolean>(false);
            const [pendingSendRequest, setPendingSendRequest] = useState<PreparedSendRequest | null>(null);
            const [allProductMatchedProducts, setAllProductMatchedProducts] = useState<AllProductMatchedProduct[]>([]);
            const [isAllProductChoiceLocked, setIsAllProductChoiceLocked] = useState<boolean>(false);
            const [allProductValidationMessage, setAllProductValidationMessage] = useState<string>("");
            const [allProductValidationLoadingPhase, setAllProductValidationLoadingPhase] = useState<SuggestionsLoadingPhase>(null);
            const [allProductSearchCitations, setAllProductSearchCitations] = useState<AllProductCitationInsight[]>([]);
            const [allProductScoreCandidates, setAllProductScoreCandidates] = useState<AllProductScoreInsight[]>([]);
            const [allProductSkipReasonCode, setAllProductSkipReasonCode] = useState<string | null>(null);
            const allProductValidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
            const allProductPrecheckRequestIdRef = useRef<number>(0);
            const validationOwnerSessionIdRef = useRef<string>(normalizedCurrentSessionId);
            const hasLiveScoreProgressRef = useRef<boolean>(false);
            const skipTemplateOnTagSelectionRef = useRef<boolean>(false);
            const currentProductSelectionSnapshotRef = useRef<ProductSelectionSnapshot>({
                selectedProduct: String(selectedProduct ?? "").trim(),
                manualKey: String(productIndex?.manual_key ?? "").trim(),
                faqKey: String(productIndex?.faq_key ?? "").trim()
            });
            const isSelectedProductAll = String(productIndex?.manual_key ?? "").trim() === "0" && String(productIndex?.faq_key ?? "").trim() === "0";
            const showAllProductValidationUI = allProductValidationStage !== "idle";
            const showAllProductValidationLoader = allProductValidationStage === "loading";
            const showAllProductValidationSelection = allProductValidationStage === "select";
            const isAllProductPrecheckLoading = showAllProductValidationLoader;
            const isQuestionInputDisabled = disableTextarea || isAllProductPrecheckLoading || isQuestionValidating;
            // Hide send button while suggestions panel is active; disable file attachment during loading
            const showSendButton =
                !sendQuestionDisabled && !answerGenerationActive && !fileUploadOrDeleteAPITriggerActive.triggerActive && !showAllProductValidationUI;
            const questionInputSurfaceBackground =
                isAllProductPrecheckLoading || isQuestionValidating ? "#e9f1ff" : isQuestionInputDisabled ? "#F2F2F2" : "#fff";
            const questionInputSurfaceBorder =
                isAllProductPrecheckLoading || isQuestionValidating ? "1px solid #c7d8ff" : isQuestionInputDisabled ? "1px solid #e4e7eb" : "1px solid #ececf1";
            const allProductValidationSearchingMessage = i18next.t("questionInput.allProductValidation.loadingPhaseSearchCitations");
            const allProductValidationScoringMessage = i18next.t("questionInput.allProductValidation.loadingPhaseScoreCandidates");
            const allProductValidationConfirmMessage = i18next.t("questionInput.allProductValidation.confirmMessage");
            const allProductValidationFallbackConfirmMessage = i18next.t("questionInput.allProductValidation.fallbackConfirmMessage");
            const allProductValidationMatchedMessage = i18next.t("questionInput.allProductValidation.matchedMessage");
            const allProductValidationContinueLabel = i18next.t("questionInput.allProductValidation.continueWithAll");
            const allProductValidationCancelLabel = i18next.t("questionInput.allProductValidation.cancel");
            const autoSelectionPhaseHoldMs = 500;
            type AutoSelectionSkipReason =
                | "disabled"
                | "no_confidence"
                | "low_hits"
                | "low_confidence"
                | "low_margin"
                | "no_reranker_scores"
                | "low_reranker_hits"
                | "low_reranker_score"
                | "low_reranker_gap";
            type RankedConfidenceEntry = { product: AllProductMatchedProduct; confidence: number };
            type RankedRerankerEntry = {
                product: AllProductMatchedProduct;
                averageScore: number;
                qualifiedCount: number;
                totalScoreCount: number;
            };
            type AutoSelectionMethod = "reranker" | "confidence" | null;
            type AutoSelectionEvaluation = {
                selectedProduct: AllProductMatchedProduct | null;
                selectionMethod: AutoSelectionMethod;
                reason: AutoSelectionSkipReason | null;
                rerankerReason: AutoSelectionSkipReason | null;
                rankedByConfidence: RankedConfidenceEntry[];
                rankedByReranker: RankedRerankerEntry[];
                topCandidate: AllProductMatchedProduct | null;
                secondCandidate: AllProductMatchedProduct | null;
                topConfidence: number | null;
                secondConfidence: number | null;
                topRerankerAverage: number | null;
                secondRerankerAverage: number | null;
                topRerankerQualifiedCount: number | null;
                secondRerankerQualifiedCount: number | null;
            };
            const parseThresholdBoolean = (value: unknown, fallback: boolean): boolean => {
                if (typeof value === "boolean") return value;
                if (typeof value === "string") {
                    const normalized = value.trim().toLowerCase();
                    if (normalized === "true") return true;
                    if (normalized === "false") return false;
                }
                return fallback;
            };
            const parseThresholdNumber = (value: unknown, fallback: number, minimum: number, maximum: number): number => {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return fallback;
                return Math.min(maximum, Math.max(minimum, numericValue));
            };
            const resolveConfiguredThresholdNumber = (value: unknown, configuredFallback: unknown, minimum: number, maximum: number): number => {
                const normalizedConfiguredFallback = parseThresholdNumber(configuredFallback, minimum, minimum, maximum);
                return parseThresholdNumber(value, normalizedConfiguredFallback, minimum, maximum);
            };
            const allProductAutoSelectSettings = useMemo(() => {
                const rawSettings = (appInfo as any)?.all_product_auto_select ?? {};
                const configuredEnabled = parseThresholdBoolean(rawSettings.enabled, parseThresholdBoolean(allProductAutoSelectStaticDefaults.enabled, true));
                const configuredMinConfidence = resolveConfiguredThresholdNumber(
                    rawSettings.min_confidence,
                    allProductAutoSelectStaticDefaults.min_confidence,
                    0,
                    100
                );
                const configuredMinMargin = resolveConfiguredThresholdNumber(rawSettings.min_margin, allProductAutoSelectStaticDefaults.min_margin, 0, 100);
                const configuredMinHits = resolveConfiguredThresholdNumber(rawSettings.min_hits, allProductAutoSelectStaticDefaults.min_hits, 1, 99);
                const configuredMinAvgRerankerScore = resolveConfiguredThresholdNumber(
                    rawSettings.min_avg_reranker_score,
                    allProductAutoSelectStaticDefaults.min_avg_reranker_score ?? 2.5,
                    0,
                    10
                );
                const configuredMinAvgRerankerGap = resolveConfiguredThresholdNumber(
                    rawSettings.min_avg_reranker_gap,
                    allProductAutoSelectStaticDefaults.min_avg_reranker_gap ?? 0.2,
                    0,
                    10
                );
                return {
                    enabled: parseThresholdBoolean(allProductAutoSelectConfig?.enabled, configuredEnabled),
                    minConfidence: parseThresholdNumber(allProductAutoSelectConfig?.minConfidence, configuredMinConfidence, 0, 100),
                    minMargin: parseThresholdNumber(allProductAutoSelectConfig?.minMargin, configuredMinMargin, 0, 100),
                    minHits: parseThresholdNumber(allProductAutoSelectConfig?.minHits, configuredMinHits, 1, 99),
                    minAvgRerankerScore: parseThresholdNumber(allProductAutoSelectConfig?.minAvgRerankerScore, configuredMinAvgRerankerScore, 0, 10),
                    minAvgRerankerGap: parseThresholdNumber(allProductAutoSelectConfig?.minAvgRerankerGap, configuredMinAvgRerankerGap, 0, 10)
                };
            }, [allProductAutoSelectConfig]);
            const rankMatchedProductsByConfidence = (matchedProducts: AllProductMatchedProduct[]): RankedConfidenceEntry[] =>
                matchedProducts
                    .map(product => {
                        const confidenceValue = Number(product.confidence);
                        return {
                            product,
                            confidence: Number.isFinite(confidenceValue) ? confidenceValue : null
                        };
                    })
                    .filter((entry): entry is { product: AllProductMatchedProduct; confidence: number } => entry.confidence !== null)
                    .sort((left, right) => {
                        if (right.confidence !== left.confidence) {
                            return right.confidence - left.confidence;
                        }
                        if (right.product.count !== left.product.count) {
                            return right.product.count - left.product.count;
                        }
                        const leftLabel = String(left.product.displayText ?? left.product.productText ?? "");
                        const rightLabel = String(right.product.displayText ?? right.product.productText ?? "");
                        return leftLabel.localeCompare(rightLabel);
                    });
            const parseRerankerScores = (product: AllProductMatchedProduct): number[] =>
                (Array.isArray(product.rerankerScores) ? product.rerankerScores : [])
                    .map(score => Number(score))
                    .filter(score => Number.isFinite(score))
                    .sort((left, right) => right - left);
            const rankMatchedProductsByReranker = (matchedProducts: AllProductMatchedProduct[]): RankedRerankerEntry[] =>
                matchedProducts
                    .map(product => {
                        const totalRerankerScores = parseRerankerScores(product);
                        const qualifiedScores = totalRerankerScores.filter(score => score >= allProductAutoSelectSettings.minAvgRerankerScore);
                        const qualifiedCount = qualifiedScores.length;
                        const averageScore = qualifiedCount > 0 ? qualifiedScores.reduce((sum, score) => sum + score, 0) / qualifiedCount : null;
                        const normalizedAverageScore = averageScore === null ? null : Number(averageScore.toFixed(4));
                        return {
                            product,
                            averageScore: normalizedAverageScore,
                            qualifiedCount,
                            totalScoreCount: totalRerankerScores.length
                        };
                    })
                    .filter(
                        (entry): entry is RankedRerankerEntry => entry.averageScore !== null && entry.qualifiedCount >= allProductAutoSelectSettings.minHits
                    )
                    .sort((left, right) => {
                        if (right.averageScore !== left.averageScore) {
                            return right.averageScore - left.averageScore;
                        }
                        if (right.qualifiedCount !== left.qualifiedCount) {
                            return right.qualifiedCount - left.qualifiedCount;
                        }
                        const leftTotalScores = Number(left.totalScoreCount ?? 0);
                        const rightTotalScores = Number(right.totalScoreCount ?? 0);
                        if (rightTotalScores !== leftTotalScores) {
                            return rightTotalScores - leftTotalScores;
                        }
                        const leftConfidence = Number(left.product.confidence);
                        const rightConfidence = Number(right.product.confidence);
                        const leftHasConfidence = Number.isFinite(leftConfidence);
                        const rightHasConfidence = Number.isFinite(rightConfidence);
                        if (leftHasConfidence && rightHasConfidence && rightConfidence !== leftConfidence) {
                            return rightConfidence - leftConfidence;
                        }
                        if (leftHasConfidence !== rightHasConfidence) {
                            return leftHasConfidence ? -1 : 1;
                        }
                        const leftLabel = String(left.product.displayText ?? left.product.productText ?? "");
                        const rightLabel = String(right.product.displayText ?? right.product.productText ?? "");
                        return leftLabel.localeCompare(rightLabel);
                    });
            const evaluateAutoSelection = (matchedProducts: AllProductMatchedProduct[]): AutoSelectionEvaluation => {
                const rankedByConfidence = rankMatchedProductsByConfidence(matchedProducts);
                const rankedByReranker = rankMatchedProductsByReranker(matchedProducts);
                const rerankerCandidates = matchedProducts
                    .map(product => {
                        const totalRerankerScores = parseRerankerScores(product);
                        const qualifiedScores = totalRerankerScores.filter(score => score >= allProductAutoSelectSettings.minAvgRerankerScore);
                        const qualifiedCount = qualifiedScores.length;
                        const averageScore =
                            qualifiedCount > 0 ? Number((qualifiedScores.reduce((sum, score) => sum + score, 0) / qualifiedCount).toFixed(4)) : null;
                        return {
                            product,
                            qualifiedCount,
                            totalScoreCount: totalRerankerScores.length,
                            averageScore
                        };
                    })
                    .sort((left, right) => {
                        const leftAverage = left.averageScore ?? Number.NEGATIVE_INFINITY;
                        const rightAverage = right.averageScore ?? Number.NEGATIVE_INFINITY;
                        if (rightAverage !== leftAverage) {
                            return rightAverage - leftAverage;
                        }
                        if (right.qualifiedCount !== left.qualifiedCount) {
                            return right.qualifiedCount - left.qualifiedCount;
                        }
                        if (right.totalScoreCount !== left.totalScoreCount) {
                            return right.totalScoreCount - left.totalScoreCount;
                        }
                        const leftLabel = String(left.product.displayText ?? left.product.productText ?? "");
                        const rightLabel = String(right.product.displayText ?? right.product.productText ?? "");
                        return leftLabel.localeCompare(rightLabel);
                    });
                const topCandidateByConfidence = rankedByConfidence[0]?.product ?? null;
                const topConfidenceByConfidence = rankedByConfidence[0]?.confidence ?? null;
                const secondConfidenceByConfidence = rankedByConfidence[1]?.confidence ?? null;
                const topRerankerAverage = rankedByReranker[0]?.averageScore ?? null;
                const secondRerankerAverage = rankedByReranker[1]?.averageScore ?? null;
                const topRerankerQualifiedCount = rankedByReranker[0]?.qualifiedCount ?? null;
                const secondRerankerQualifiedCount = rankedByReranker[1]?.qualifiedCount ?? null;
                let rerankerReason: AutoSelectionSkipReason | null = null;

                if (!allProductAutoSelectSettings.enabled || matchedProducts.length === 0) {
                    return {
                        selectedProduct: null,
                        selectionMethod: null,
                        reason: "disabled",
                        rerankerReason: "disabled",
                        rankedByConfidence,
                        rankedByReranker,
                        topCandidate: topCandidateByConfidence,
                        secondCandidate: rankedByConfidence[1]?.product ?? null,
                        topConfidence: topConfidenceByConfidence,
                        secondConfidence: secondConfidenceByConfidence,
                        topRerankerAverage,
                        secondRerankerAverage,
                        topRerankerQualifiedCount,
                        secondRerankerQualifiedCount
                    };
                }

                if (rankedByReranker.length > 0 && topRerankerAverage !== null) {
                    const rerankerLead = secondRerankerAverage === null ? Number.POSITIVE_INFINITY : topRerankerAverage - secondRerankerAverage;
                    if (
                        topRerankerAverage >= allProductAutoSelectSettings.minAvgRerankerScore &&
                        rerankerLead >= allProductAutoSelectSettings.minAvgRerankerGap
                    ) {
                        const selectedProduct = rankedByReranker[0].product;
                        const secondCandidate = rankedByReranker[1]?.product ?? null;
                        const selectedConfidence = Number(selectedProduct.confidence);
                        const secondConfidence = Number(secondCandidate?.confidence);
                        return {
                            selectedProduct,
                            selectionMethod: "reranker",
                            reason: null,
                            rerankerReason: null,
                            rankedByConfidence,
                            rankedByReranker,
                            topCandidate: selectedProduct,
                            secondCandidate,
                            topConfidence: Number.isFinite(selectedConfidence) ? selectedConfidence : null,
                            secondConfidence: Number.isFinite(secondConfidence) ? secondConfidence : null,
                            topRerankerAverage,
                            secondRerankerAverage,
                            topRerankerQualifiedCount,
                            secondRerankerQualifiedCount
                        };
                    }
                    rerankerReason = "low_reranker_gap";
                } else {
                    const topRerankerCandidate = rerankerCandidates[0] ?? null;
                    if (!topRerankerCandidate || topRerankerCandidate.totalScoreCount === 0) {
                        rerankerReason = "no_reranker_scores";
                    } else if (topRerankerCandidate.qualifiedCount < allProductAutoSelectSettings.minHits) {
                        rerankerReason = "low_reranker_hits";
                    } else {
                        rerankerReason = "low_reranker_score";
                    }
                }

                if (rankedByConfidence.length === 0) {
                    return {
                        selectedProduct: null,
                        selectionMethod: null,
                        reason: "no_confidence",
                        rerankerReason,
                        rankedByConfidence,
                        rankedByReranker,
                        topCandidate: topCandidateByConfidence,
                        secondCandidate: rankedByConfidence[1]?.product ?? null,
                        topConfidence: topConfidenceByConfidence,
                        secondConfidence: secondConfidenceByConfidence,
                        topRerankerAverage,
                        secondRerankerAverage,
                        topRerankerQualifiedCount,
                        secondRerankerQualifiedCount
                    };
                }

                if ((topCandidateByConfidence?.count ?? 0) < allProductAutoSelectSettings.minHits) {
                    return {
                        selectedProduct: null,
                        selectionMethod: null,
                        reason: "low_hits",
                        rerankerReason,
                        rankedByConfidence,
                        rankedByReranker,
                        topCandidate: topCandidateByConfidence,
                        secondCandidate: rankedByConfidence[1]?.product ?? null,
                        topConfidence: topConfidenceByConfidence,
                        secondConfidence: secondConfidenceByConfidence,
                        topRerankerAverage,
                        secondRerankerAverage,
                        topRerankerQualifiedCount,
                        secondRerankerQualifiedCount
                    };
                }
                if ((topConfidenceByConfidence ?? -1) < allProductAutoSelectSettings.minConfidence) {
                    return {
                        selectedProduct: null,
                        selectionMethod: null,
                        reason: "low_confidence",
                        rerankerReason,
                        rankedByConfidence,
                        rankedByReranker,
                        topCandidate: topCandidateByConfidence,
                        secondCandidate: rankedByConfidence[1]?.product ?? null,
                        topConfidence: topConfidenceByConfidence,
                        secondConfidence: secondConfidenceByConfidence,
                        topRerankerAverage,
                        secondRerankerAverage,
                        topRerankerQualifiedCount,
                        secondRerankerQualifiedCount
                    };
                }
                if (
                    Number.isFinite(secondConfidenceByConfidence) &&
                    (topConfidenceByConfidence as number) - (secondConfidenceByConfidence as number) < allProductAutoSelectSettings.minMargin
                ) {
                    return {
                        selectedProduct: null,
                        selectionMethod: null,
                        reason: "low_margin",
                        rerankerReason,
                        rankedByConfidence,
                        rankedByReranker,
                        topCandidate: topCandidateByConfidence,
                        secondCandidate: rankedByConfidence[1]?.product ?? null,
                        topConfidence: topConfidenceByConfidence,
                        secondConfidence: secondConfidenceByConfidence,
                        topRerankerAverage,
                        secondRerankerAverage,
                        topRerankerQualifiedCount,
                        secondRerankerQualifiedCount
                    };
                }

                return {
                    selectedProduct: topCandidateByConfidence,
                    selectionMethod: "confidence",
                    reason: null,
                    rerankerReason,
                    rankedByConfidence,
                    rankedByReranker,
                    topCandidate: topCandidateByConfidence,
                    secondCandidate: rankedByConfidence[1]?.product ?? null,
                    topConfidence: topConfidenceByConfidence,
                    secondConfidence: secondConfidenceByConfidence,
                    topRerankerAverage,
                    secondRerankerAverage,
                    topRerankerQualifiedCount,
                    secondRerankerQualifiedCount
                };
            };
            const buildAutoSelectionSkippedMessage = (evaluation: AutoSelectionEvaluation): string | null => {
                if (!evaluation.reason || evaluation.reason === "disabled") return null;
                const topCandidateName = evaluation.topCandidate?.displayText ?? evaluation.topCandidate?.productText ?? "-";
                const topConfidenceValue = Math.max(0, Math.round(Number(evaluation.topConfidence ?? 0)));
                const secondConfidenceValue = Number.isFinite(Number(evaluation.secondConfidence))
                    ? Math.max(0, Math.round(Number(evaluation.secondConfidence)))
                    : null;
                const marginValue = secondConfidenceValue === null ? null : Math.max(0, Math.round(topConfidenceValue - secondConfidenceValue));

                if (evaluation.reason === "no_confidence") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedNoConfidence");
                }
                if (evaluation.reason === "low_hits") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedLowHits", {
                        product: topCandidateName,
                        hits: evaluation.topCandidate?.count ?? 0,
                        requiredHits: allProductAutoSelectSettings.minHits
                    });
                }
                if (evaluation.reason === "low_confidence") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedLowConfidence", {
                        product: topCandidateName,
                        topConfidence: topConfidenceValue,
                        requiredConfidence: allProductAutoSelectSettings.minConfidence
                    });
                }
                if (evaluation.reason === "low_margin") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedLowMargin", {
                        product: topCandidateName,
                        topConfidence: topConfidenceValue,
                        secondConfidence: secondConfidenceValue ?? "-",
                        margin: marginValue ?? "-",
                        requiredMargin: allProductAutoSelectSettings.minMargin
                    });
                }
                if (evaluation.reason === "no_reranker_scores") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedNoRerankerScores");
                }
                if (evaluation.reason === "low_reranker_hits") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedLowRerankerHits", {
                        product: topCandidateName,
                        hits: evaluation.topRerankerQualifiedCount ?? 0,
                        requiredHits: allProductAutoSelectSettings.minHits,
                        minScore: Number(allProductAutoSelectSettings.minAvgRerankerScore).toFixed(2)
                    });
                }
                if (evaluation.reason === "low_reranker_score") {
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedLowRerankerScore", {
                        product: topCandidateName,
                        topScore: evaluation.topRerankerAverage === null ? "-" : Number(evaluation.topRerankerAverage).toFixed(2),
                        requiredScore: Number(allProductAutoSelectSettings.minAvgRerankerScore).toFixed(2)
                    });
                }
                if (evaluation.reason === "low_reranker_gap") {
                    const rerankerGap =
                        evaluation.topRerankerAverage !== null && evaluation.secondRerankerAverage !== null
                            ? Number((evaluation.topRerankerAverage - evaluation.secondRerankerAverage).toFixed(4))
                            : null;
                    return i18next.t("questionInput.allProductValidation.autoSelectionSkippedLowRerankerGap", {
                        product: topCandidateName,
                        gap: rerankerGap === null ? "-" : rerankerGap.toFixed(2),
                        requiredGap: Number(allProductAutoSelectSettings.minAvgRerankerGap).toFixed(2)
                    });
                }
                return null;
            };
            const buildScoreCandidatesFromMatchedProducts = (matchedProducts: AllProductMatchedProduct[]): AllProductScoreInsight[] =>
                matchedProducts
                    .map(product => ({
                        id: [
                            product.manual_key,
                            product.faq_key,
                            product.productVersion?.manual_version_key ?? "",
                            product.productVersion?.faq_version_key ?? "",
                            product.subProduct?.manual_sub_key ?? "",
                            product.subProduct?.faq_sub_key ?? ""
                        ].join("|"),
                        productLabel: String(product.displayText ?? product.productText ?? "-"),
                        confidence: Number.isFinite(Number(product.confidence)) ? Math.round(Number(product.confidence)) : null,
                        citationCount: Math.max(0, Number(product.count ?? 0)),
                        averageScore: Number.isFinite(Number(product.averageScore)) ? Number(product.averageScore) : null,
                        maxScore: Number.isFinite(Number(product.maxScore)) ? Number(product.maxScore) : null,
                        scoreSampleCount: Number.isFinite(Number(product.scoreSampleCount)) ? Math.max(0, Number(product.scoreSampleCount)) : 0
                    }))
                    .sort((left, right) => {
                        const leftConfidence = Number(left.confidence ?? -1);
                        const rightConfidence = Number(right.confidence ?? -1);
                        if (rightConfidence !== leftConfidence) return rightConfidence - leftConfidence;
                        if (right.citationCount !== left.citationCount) return right.citationCount - left.citationCount;
                        return left.productLabel.localeCompare(right.productLabel);
                    });
            const normalizeConfidencePercent = (confidence: number | null): number | null => {
                const numericConfidence = Number(confidence);
                if (!Number.isFinite(numericConfidence)) {
                    return null;
                }
                return Math.max(0, Math.min(100, Math.round(numericConfidence)));
            };
            const buildAutoSelectionMeta = (evaluation: AutoSelectionEvaluation, matchedProducts: AllProductMatchedProduct[]): AllProductSelectionMeta => {
                const selectedProduct = evaluation.selectedProduct ?? evaluation.topCandidate;
                const selectedProductLabel = selectedProduct?.displayText ?? selectedProduct?.productText ?? "-";
                const selectedHits = Number.isFinite(Number(selectedProduct?.count)) ? Math.max(0, Number(selectedProduct?.count ?? 0)) : 0;
                const secondProduct = evaluation.secondCandidate ?? null;
                const secondProductLabel = secondProduct?.displayText ?? secondProduct?.productText ?? null;
                const selectedConfidence = normalizeConfidencePercent(
                    evaluation.topConfidence ?? (Number.isFinite(Number(selectedProduct?.confidence)) ? Number(selectedProduct?.confidence) : null)
                );
                const secondConfidence = normalizeConfidencePercent(
                    evaluation.secondConfidence ?? (Number.isFinite(Number(secondProduct?.confidence)) ? Number(secondProduct?.confidence) : null)
                );
                const confidenceMargin = selectedConfidence !== null && secondConfidence !== null ? Math.max(0, selectedConfidence - secondConfidence) : null;
                const citationCountFromHits = matchedProducts.reduce(
                    (sum, product) => sum + (Number.isFinite(Number(product.count)) ? Math.max(0, Number(product.count)) : 0),
                    0
                );
                const rerankerGap =
                    evaluation.topRerankerAverage !== null && evaluation.secondRerankerAverage !== null
                        ? Number((evaluation.topRerankerAverage - evaluation.secondRerankerAverage).toFixed(4))
                        : null;
                return {
                    source: "auto",
                    autoSelectionSnapshot: {
                        selectedProductLabel,
                        selectedConfidence,
                        selectedHits,
                        secondProductLabel,
                        secondConfidence,
                        confidenceMargin,
                        candidateCount: matchedProducts.length,
                        citationCount: citationCountFromHits,
                        minConfidence: allProductAutoSelectSettings.minConfidence,
                        minHits: allProductAutoSelectSettings.minHits,
                        minMargin: allProductAutoSelectSettings.minMargin,
                        selectionMethod: evaluation.selectionMethod ?? "confidence",
                        selectedAverageRerankerScore: evaluation.topRerankerAverage,
                        secondAverageRerankerScore: evaluation.secondRerankerAverage,
                        selectedRerankerQualifiedCount: evaluation.topRerankerQualifiedCount,
                        secondRerankerQualifiedCount: evaluation.secondRerankerQualifiedCount,
                        rerankerGap,
                        minAvgRerankerScore: allProductAutoSelectSettings.minAvgRerankerScore,
                        minAvgRerankerGap: allProductAutoSelectSettings.minAvgRerankerGap
                    }
                };
            };
            const buildBackendAutoSelectionMeta = (
                selectedProductOption: AllProductMatchedProduct,
                matchedProducts: AllProductMatchedProduct[],
                selectionDecision: AllProductPrecheckSelectionDecision
            ): AllProductSelectionMeta => {
                const selectedProductLabel = selectedProductOption.displayText ?? selectedProductOption.productText ?? "-";
                const citationCount = matchedProducts.reduce((sum, p) => sum + (Number.isFinite(Number(p.count)) ? Math.max(0, Number(p.count)) : 0), 0);
                return {
                    source: "auto",
                    autoSelectionSnapshot: {
                        selectedProductLabel,
                        modelSelectedProductLabel: selectionDecision.selected_display_label ?? selectedProductLabel,
                        customerConfigApplied: selectionDecision.customer_config_applied ?? false,
                        modelSelectedScore: selectionDecision.selected_score ?? null,
                        modelSelectedCitationCount: selectionDecision.selected_citation_count ?? null,
                        ptfSelectedScore: selectionDecision.ptf_selected_score ?? null,
                        ptfSelectedCitationCount: selectionDecision.ptf_selected_citation_count ?? null,
                        ptfSelectedProductIndexes: selectionDecision.ptf_selected_product_indexes ?? null,
                        ptfSelectedVersionIndexes: selectionDecision.ptf_selected_version_indexes ?? null,
                        ptfSelectedSubProductIndexes: selectionDecision.ptf_selected_sub_product_indexes ?? null,
                        precheckCandidates: selectionDecision.candidates ?? null,
                        customerConfigRegistered: selectionDecision.customer_config_registered ?? false,
                        customerConfigVersionMajor: selectionDecision.customer_config_version_major ?? null,
                        customerConfigEffectiveVersionMajor: selectionDecision.customer_config_effective_version_major ?? null,
                        customerConfigComparison: selectionDecision.customer_config_comparison ?? null,
                        decisionSource: "server_model",
                        decisionReason: selectionDecision.reason ?? null,
                        decisionReasonCode: selectionDecision.reason_code ?? null,
                        selectedConfidence: normalizeConfidencePercent(
                            Number.isFinite(Number(selectedProductOption.confidence)) ? Number(selectedProductOption.confidence) : null
                        ),
                        selectedHits: Number.isFinite(Number(selectedProductOption.count)) ? Math.max(0, Number(selectedProductOption.count)) : 0,
                        secondProductLabel: null,
                        secondConfidence: null,
                        confidenceMargin: null,
                        candidateCount: matchedProducts.length,
                        citationCount,
                        minConfidence: allProductAutoSelectSettings.minConfidence,
                        minHits: allProductAutoSelectSettings.minHits,
                        minMargin: allProductAutoSelectSettings.minMargin
                    }
                };
            };
            const createProductSelectionSnapshot = (): ProductSelectionSnapshot => ({
                selectedProduct: String(selectedProduct ?? "").trim(),
                manualKey: String(productIndex?.manual_key ?? "").trim(),
                faqKey: String(productIndex?.faq_key ?? "").trim()
            });
            const isAllSelectionSnapshot = (snapshot: ProductSelectionSnapshot): boolean => snapshot.manualKey === "0" && snapshot.faqKey === "0";
            const areSelectionSnapshotsEqual = (left: ProductSelectionSnapshot, right: ProductSelectionSnapshot): boolean =>
                left.selectedProduct === right.selectedProduct && left.manualKey === right.manualKey && left.faqKey === right.faqKey;
            const [tourFileAttachment, setTourFileAttachment] = useSetState<TourState>({
                run:
                    (userInfo.fileAttachmentTourDone === "false" || !userInfo.fileAttachmentTourDone) && userInfo.chatTourDone === "true"
                        ? true
                        : false && userInfo.templateTourDone === "true"
                          ? true
                          : false,
                steps: [
                    {
                        content: i18next.t(`appTour.fileAttachmentTour.fileAttachmentBtn`),
                        target: "#FileAttachmentBtn",
                        disableBeacon: true,
                        floaterProps: {
                            disableAnimation: true,
                            autoOpen: true
                        }
                    }
                ]
            });

            const clearAllProductValidationTimer = useCallback(() => {
                if (allProductValidationTimerRef.current) {
                    clearTimeout(allProductValidationTimerRef.current);
                    allProductValidationTimerRef.current = null;
                }
            }, []);

            const resetAllProductValidationState = useCallback(() => {
                clearAllProductValidationTimer();
                onAbortAllProductPrecheck?.();
                allProductPrecheckRequestIdRef.current += 1;
                hasLiveScoreProgressRef.current = false;
                setAllProductValidationStage("idle");
                setIsQuestionValidating(false);
                setPendingSendRequest(null);
                setAllProductMatchedProducts([]);
                setAllProductSearchCitations([]);
                setAllProductScoreCandidates([]);
                setIsAllProductChoiceLocked(false);
                setAllProductValidationMessage("");
                setAllProductValidationLoadingPhase(null);
                setAllProductSkipReasonCode(null);
            }, [clearAllProductValidationTimer, onAbortAllProductPrecheck]);

            // ── Sync local precheck state to Redux so the side panel can read it ──
            useEffect(() => {
                if (validationOwnerSessionIdRef.current !== normalizedCurrentSessionId) {
                    return;
                }
                dispatch(
                    AllProductSuggestionsAction.syncSuggestionsState({
                        stage: allProductValidationStage,
                        matchedProducts: allProductMatchedProducts,
                        message: allProductValidationMessage,
                        isLocked: isAllProductChoiceLocked,
                        loadingPhase: allProductValidationLoadingPhase,
                        searchCitations: allProductSearchCitations,
                        scoreCandidates: allProductScoreCandidates,
                        skipReasonCode: allProductSkipReasonCode
                    })
                );
            }, [
                allProductValidationStage,
                allProductMatchedProducts,
                allProductValidationMessage,
                isAllProductChoiceLocked,
                allProductValidationLoadingPhase,
                allProductSearchCitations,
                allProductScoreCandidates,
                allProductSkipReasonCode,
                normalizedCurrentSessionId
            ]);

            useEffect(() => {
                if (validationOwnerSessionIdRef.current === normalizedCurrentSessionId) {
                    return;
                }
                validationOwnerSessionIdRef.current = normalizedCurrentSessionId;
                resetAllProductValidationState();
                dispatch(AllProductSuggestionsAction.resetSuggestions());
            }, [dispatch, normalizedCurrentSessionId, resetAllProductValidationState]);

            // ── Handle action intents dispatched by the side panel ──────────────
            const pendingSuggestionsAction = useSelector(selectSuggestionsPendingAction);
            const panelSuggestionsStage = useSelector(selectSuggestionsStage);
            // Show inline validation UI only when the Redux side panel hasn't taken over yet.
            // In tests, mock dispatch never updates mockState so panelSuggestionsStage stays
            // "idle" — meaning the inline UI is always visible and interactive for tests.
            const shouldShowInlineValidationUI = showInlineAllProductValidationFallback && showAllProductValidationUI && panelSuggestionsStage === "idle";
            useEffect(() => {
                if (!pendingSuggestionsAction) return;
                const isSameValidationSession = validationOwnerSessionIdRef.current === normalizedCurrentSessionId;
                if (!isSameValidationSession) {
                    dispatch(AllProductSuggestionsAction.clearPendingAction());
                    return;
                }

                if (pendingSuggestionsAction.type === "selectProduct") {
                    // Guard against transient mounts/instances consuming and clearing the action
                    // before the input that owns the active precheck request can apply it.
                    if (isAllProductChoiceLocked || !pendingSendRequest) {
                        return;
                    }
                    dispatch(AllProductSuggestionsAction.clearPendingAction());
                    handleMatchedProductSelection(pendingSuggestionsAction.product);
                } else if (pendingSuggestionsAction.type === "continueWithAll") {
                    if (isAllProductChoiceLocked) {
                        return;
                    }
                    dispatch(AllProductSuggestionsAction.clearPendingAction());
                    handleContinueWithAll();
                } else if (pendingSuggestionsAction.type === "cancel") {
                    if (isAllProductChoiceLocked) {
                        return;
                    }
                    dispatch(AllProductSuggestionsAction.clearPendingAction());
                    handleCancelAllProductValidation();
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [dispatch, isAllProductChoiceLocked, normalizedCurrentSessionId, pendingSendRequest, pendingSuggestionsAction]);

            useEffect(() => {
                currentProductSelectionSnapshotRef.current = createProductSelectionSnapshot();
            }, [selectedProduct, productIndex?.manual_key, productIndex?.faq_key]);

            useEffect(() => {
                return () => {
                    clearAllProductValidationTimer();
                };
            }, [clearAllProductValidationTimer]);

            useEffect(() => {
                resetAllProductValidationState();
            }, [selectedProduct, productIndex?.manual_key, productIndex?.faq_key, resetAllProductValidationState]);

            useEffect(() => {
                if (clearChatTriggered || isNewChatThreadInitialLoading) {
                    resetAllProductValidationState();
                    setQuestion("");
                    setDefaultContentState("");
                    setIsEdited(false);
                    setQuestionContainsTemplate(false);
                }
            }, [clearChatTriggered, isNewChatThreadInitialLoading, resetAllProductValidationState]);

            useEffect(() => {
                if (skipTemplateOnTagSelectionRef.current) {
                    return;
                }
                if (isHistoryPageInitialLoading && !requestedToForceStopAIResponseGeneration) {
                    setQuestion("");
                    setDefaultContentState("");
                } else {
                    // history page load done & some operations like product change or a new question is already asked
                    //  or a template is applied after history page initial load.

                    // or the page is a new Chat conversation page.
                    const userFavouriteContentForSelectedProduct = userInfo["userFavourite"]?.[selectedProduct];
                    const templateEntries = Array.isArray(templateContents)
                        ? (templateContents as Array<{ id?: string; product?: string; template?: any[] }>)
                        : [];
                    const selectedTemplateEntry = selectedTemplateContentId
                        ? templateEntries.find(tempContent => tempContent.id === selectedTemplateContentId)
                        : null;
                    const selectedTemplateMatchesCurrentProduct = !!(
                        selectedTemplateEntry &&
                        (selectedTemplateEntry.product === selectedProduct || selectedTemplateEntry.product === "ALL")
                    );
                    const hasNoFavouriteSelection =
                        userFavouriteContentForSelectedProduct === "None" ||
                        userFavouriteContentForSelectedProduct === "" ||
                        userFavouriteContentForSelectedProduct === null ||
                        userFavouriteContentForSelectedProduct === undefined;

                    if (!requestedToForceStopAIResponseGeneration && hasNoFavouriteSelection) {
                        if (selectedTemplateMatchesCurrentProduct) {
                            const selectedTemplatePromptSource =
                                templateContent && Object.keys(templateContent).length > 0 ? templateContent : selectedTemplateEntry?.template?.[0];
                            const selectedTemplatePrompt = getTemplatePromptTextValue(selectedTemplatePromptSource);
                            setDefaultContentState(selectedTemplatePrompt);
                            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(!!selectedTemplatePrompt));
                        } else {
                            setDefaultContentState("");
                            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                        }
                        return;
                    }
                    if (
                        !!(selectedTemplateContentId && selectedTemplateContentId !== "") &&
                        !changingTemplateContent &&
                        !isEdited &&
                        userFavouriteContentForSelectedProduct &&
                        userFavouriteContentForSelectedProduct === selectedTemplateContentId &&
                        !requestedToForceStopAIResponseGeneration
                    ) {
                        const templateValue = getTemplatePromptTextValueOnPageLoad(templateContent, selectedTemplateContentId);
                        setDefaultContentState(templateValue);
                        setTimeout(() => {
                            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(true));
                        }, 100);
                        return;
                    } else {
                        if (!requestedToForceStopAIResponseGeneration && (clearChatTriggered || !chatContentCleared)) {
                            const adminQuestionInput = userInfo["userFavourite"]?.hasOwnProperty(selectedProduct)
                                ? userDefaultContent === "" || userDefaultContent === undefined
                                    ? title.product !== selectedProduct
                                        ? userInfo["userFavourite"][selectedProduct] !== "" && userInfo["userFavourite"][selectedProduct] !== "None"
                                            ? getTemplatePromptTextValueOnPageLoad(title.template[0], title.id)
                                            : userInfo["userFavourite"][selectedProduct] === "None"
                                              ? ""
                                              : getSystemDefaultTemplatePromptText(false)
                                        : validateConditionsAndApplyUserTemplate()
                                    : userDefaultContent === ""
                                      ? getSystemDefaultTemplatePromptText(false)
                                      : validateConditionsAndApplyUserTemplate()
                                : userInfo["userFavourite"]?.["ALL"] !== null
                                  ? title.product === selectedProduct
                                      ? getTemplatePromptTextValueOnPageLoad(allOptionsUserDefault?.template?.[0], allOptionsUserDefault?.id)
                                      : getSystemDefaultTemplatePromptText(false)
                                  : "";

                            const nonAdminQuestionInput = userInfo["userFavourite"]?.hasOwnProperty(selectedProduct)
                                ? userDefaultContent === "" || userDefaultContent === undefined
                                    ? title.product !== selectedProduct
                                        ? userInfo["userFavourite"][selectedProduct] !== "" && userInfo["userFavourite"][selectedProduct] !== "None"
                                            ? getTemplatePromptTextValueOnPageLoad(title.template[0], title.id)
                                            : userInfo["userFavourite"][selectedProduct] === "None"
                                              ? ""
                                              : getSystemDefaultTemplatePromptText(false)
                                        : validateConditionsAndApplyUserTemplate()
                                    : userDefaultContent === ""
                                      ? getSystemDefaultTemplatePromptText(false)
                                      : validateConditionsAndApplyUserTemplate()
                                : userInfo["userFavourite"]?.["ALL"] !== null
                                  ? title.product === selectedProduct
                                      ? getTemplatePromptTextValueOnPageLoad(allOptionsUserDefault?.template?.[0], allOptionsUserDefault?.id)
                                      : getSystemDefaultTemplatePromptText(false)
                                  : "";
                            const defaultContentStateValue = isAdmin
                                ? chatContentCleared
                                    ? ""
                                    : adminQuestionInput
                                : chatContentCleared
                                  ? ""
                                  : nonAdminQuestionInput;
                            setDefaultContentState(defaultContentStateValue);
                            if (!chatContentCleared) {
                                const userTemplateDataExists = userInfo["userFavourite"]?.hasOwnProperty(selectedProduct);
                                const systemDefaultData = getSharedSystemDefaultTemplate(selectedProduct, templateContents);
                                if (userTemplateDataExists && userInfo["userFavourite"]?.[selectedProduct] === "None") {
                                    const hasTemplateSelection = !!(selectedTemplateContentId && selectedTemplateContentId !== "");
                                    dispatch(
                                        QuestionInputReducerAction.updateTemplateAppliedFlag(
                                            hasTemplateSelection || !!(defaultContentStateValue && defaultContentStateValue !== "")
                                        )
                                    );
                                } else if ((userTemplateDataExists && userInfo["userFavourite"]?.[selectedProduct] !== "None") || systemDefaultData) {
                                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(true));
                                } else {
                                    dispatch(
                                        QuestionInputReducerAction.updateTemplateAppliedFlag(!!(defaultContentStateValue && defaultContentStateValue !== ""))
                                    );
                                }
                            }
                        }
                    }
                }
            }, [
                userDefaultContent,
                selectedProduct,
                selectedTemplateContentId,
                templateContent,
                templateContents,
                clearChatTriggered,
                chatContentCleared,
                isHistoryPageInitialLoading,
                isNewChatThreadInitialLoading,
                userInfo["userFavourite"]?.[selectedProduct],
                allOptionsUserDefault
            ]);

            useEffect(() => {
                const previousSelectedProduct = previousSelectedProductRef.current;
                const hasSelectedProductChanged =
                    previousSelectedProduct !== null && String(previousSelectedProduct ?? "").trim() !== String(selectedProduct ?? "").trim();

                if (hasSelectedProductChanged) {
                    setQuestion("");
                    setDefaultContentState("");
                    setIsEdited(false);
                    setQuestionContainsTemplate(false);
                    setChangingTemplateContent?.(false);
                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                }

                previousSelectedProductRef.current = selectedProduct;
            }, [dispatch, selectedProduct, setChangingTemplateContent, setIsEdited]);

            useEffect(() => {
                if (!isEdited) {
                    const templateValue = getTemplatePromptTextValue(templateContent);
                    setQuestion(templateValue);
                    if (templateValue) {
                        dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(true));
                        setDefaultContentState(templateValue);
                    }
                    if (!clearChatTriggered) {
                        setDefaultContentState("");
                        setQuestion("");
                    } else {
                        return;
                    }
                }
            }, [templateContent, isEdited, clearChatTriggered]);

            useEffect(() => {
                if (templateAppliedFlagValue) {
                    const questionContainsTemplateFlag = checkIfquestionContainsTemplate();
                    setQuestionContainsTemplate(questionContainsTemplateFlag);
                } else {
                    setQuestionContainsTemplate(false);
                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                }
                if (undoStack.length === 1) {
                    const currentTextValue = (
                        !!(defaultContentState && defaultContentState !== "")
                            ? defaultContentState
                            : isEdited
                              ? question
                              : getTemplatePromptTextValue(templateContent)
                    ).trim();
                    if (!!(currentTextValue && currentTextValue !== "")) {
                        dispatch(QuestionInputReducerAction.addToQuestionInputUndoHistoryStack(currentTextValue));
                    }
                }
            }, [templateContent, isEdited, clearChatTriggered, defaultContentState, question, undoStack]);

            const normalizeQuestionInputText = (value: string): string =>
                String(value ?? "")
                    .replace(/\u00a0/g, " ")
                    .trim();

            useEffect(() => {
                const currentQuestionInputTextValue: string = getCurrentQuestionInputValue();
                const isCurrentQuestionInputEmpty = normalizeQuestionInputText(currentQuestionInputTextValue) === "";
                const isInvalidProductSelection =
                    (selectedAppType === "Manuals" && productIndex?.manual_key === "") ||
                    (selectedAppType === "FAQ" && productIndex?.faq_key === "") ||
                    (selectedAppType === "ALL" && productIndex?.manual_key === "" && productIndex?.faq_key === "");

                if (disabled || isCurrentQuestionInputEmpty) {
                    setSendQuestionDisabled(true);
                    return;
                }

                if (
                    templateAppliedFlagValue &&
                    !!(templateContent && templateContent !== "" && selectedTemplateContentId && selectedTemplateContentId !== "")
                ) {
                    const templatePromptText = normalizeQuestionInputText(getTemplatePromptTextValue(templateContent));
                    const normalizedCurrentQuestionInputText = normalizeQuestionInputText(currentQuestionInputTextValue);
                    let questionInputTemplatePromptEdited = templatePromptText !== normalizedCurrentQuestionInputText;
                    if (!questionInputTemplatePromptEdited) {
                        questionInputTemplatePromptEdited = templateContent?.isSystemTemplate === false;
                    }
                    setSendQuestionDisabled(
                        templateContent?.isSystemTemplate
                            ? isInvalidProductSelection || !questionInputTemplatePromptEdited
                            : isInvalidProductSelection && !questionInputTemplatePromptEdited
                    );
                    return;
                }

                setSendQuestionDisabled(isInvalidProductSelection);
            }, [
                disabled,
                isEdited,
                selectedAppType,
                productIndex?.manual_key,
                productIndex?.faq_key,
                templateAppliedFlagValue,
                templateContent,
                selectedTemplateContentId,
                defaultContentState,
                question
            ]);

            useImperativeHandle(ref, () => ({
                clearTemplateContentFromQuestionInput: () => clearInput(true),
                clearAfterExternalSend: () => clearInputAfterSend(),
                getCurrentQuestionInputValue: () => getCurrentQuestionInputValue(),
                onForceStopAIResponseGenerationRetainQuestion: (answerGenerationActiveDetails: AnswerGenerationActiveDetails) =>
                    onForceStopAIResponseGenerationRetainQuestion(answerGenerationActiveDetails),
                setQuestionInputValue: (value: string) => {
                    resetAllProductValidationState();
                    setQuestion(value);
                    setDefaultContentState(value);
                    setIsEdited(true);
                    setQuestionContainsTemplate(false);
                    setChangingTemplateContent?.(false);
                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                },
                triggerSendWithQuestion: (question: string) => {
                    sendQuestion(question);
                }
            }));

            useEffect(() => {
                if (mentionsInputRef.current && !activeFileAttachmentTour) {
                    mentionsInputRef.current.focus();
                    const length = focusPositionToRetain ? focusPositionToRetain : mentionsInputRef.current.value.length;
                    mentionsInputRef.current.setSelectionRange(length, length);
                }
                if (
                    userInfo.templateTourDone === "true" &&
                    (activeFileAttachmentTour || userInfo.fileAttachmentTourDone === "false" || !userInfo.fileAttachmentTourDone)
                ) {
                    setTourFileAttachment(prevState => ({
                        ...prevState,
                        run: true
                    }));
                }
            }, [templateContent, defaultContentState, answers, activeFileAttachmentTour]);

            const formattedTemplateTitles = templateTitles.slice(0, 5).map(title => ({
                id: title,
                display: title
            }));

            const executeSendWithRequest = (sendRequest: PreparedSendRequest) => {
                dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                setSelectedAppType?.(sendRequest.appTypeBasedOnUser);
                onSend(sendRequest.questionText, sendRequest.questionContainsTemplateValue, sendRequest.appTypeBasedOnUser);
                clearInputAfterSend();
            };

            const clearInputAfterSend = () => {
                if (clearOnSend) {
                    setQuestion("");
                    setIsEdited(false);
                    setDefaultContentState("");
                    setChangingTemplateContent && setChangingTemplateContent(false);
                    setChatContentCleared?.(true);
                    if (mentionsInputRef.current) {
                        mentionsInputRef.current.value = "";
                    }
                    if (setClearChatTriggered) {
                        setClearChatTriggered(false);
                    }
                }
            };

            const applyMatchedProductSelection = async (
                selectedProductOption: AllProductMatchedProduct,
                sendRequest: PreparedSendRequest,
                selectionMeta?: AllProductSelectionMeta
            ): Promise<boolean> => {
                if (!onAllProductTagSelected) {
                    return false;
                }

                setIsAllProductChoiceLocked(true);
                skipTemplateOnTagSelectionRef.current = true;
                try {
                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                    await onAllProductTagSelected(selectedProductOption, sendRequest, selectionMeta);
                    clearInputAfterSend();
                    resetAllProductValidationState();
                    return true;
                } catch (error) {
                    console.error("Error while applying matched product selection:", error);
                    setIsAllProductChoiceLocked(false);
                    return false;
                } finally {
                    skipTemplateOnTagSelectionRef.current = false;
                }
            };

            const sendQuestion = async (overrideQuestion?: string) => {
                const currentQuestionInputTextValue: string = overrideQuestion ?? getCurrentQuestionInputValue();
                if (disabled || !currentQuestionInputTextValue.trim()) {
                    return;
                }

                if (
                    (selectedAppType === "Manuals" && productIndex?.manual_key === "") ||
                    (selectedAppType === "FAQ" && productIndex?.faq_key === "") ||
                    (selectedAppType === "ALL" && productIndex?.manual_key === "" && productIndex?.faq_key === "")
                ) {
                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                    onSend("invalid_selection", false, selectedAppType);
                    return;
                }
                const filteredQuestion = currentQuestionInputTextValue.match(/@\[(.*?)\]/)?.[1];
                const questionInputTextValue = filteredQuestion ? filteredQuestion : currentQuestionInputTextValue;
                const appTypeValue: AppType = getAppTypeBasedOnSelectedValueOnProductFilter();
                const appTypeBasedOnUser = getAppTypeBasedOnUser(appTypeValue);
                const sendRequest: PreparedSendRequest = {
                    rawQuestionText: currentQuestionInputTextValue,
                    questionText: questionInputTextValue,
                    questionContainsTemplateValue: templateAppliedFlagValue ?? questionContainsTemplate,
                    appTypeBasedOnUser,
                    sourceMode: mode
                };

                if (isSelectedProductAll) {
                    const precheckSessionId = normalizeSessionIdentity(store.getState().chatComponent.currentSessionId);
                    validationOwnerSessionIdRef.current = precheckSessionId;
                    clearAllProductValidationTimer();
                    setPendingSendRequest(sendRequest);
                    setAllProductMatchedProducts([]);
                    setAllProductSearchCitations([]);
                    setAllProductScoreCandidates([]);
                    setAllProductValidationMessage(allProductValidationSearchingMessage);
                    setAllProductValidationLoadingPhase("search");
                    setIsAllProductChoiceLocked(false);
                    // Show progress bar at input bottom instead of immediately opening the suggestion panel.
                    // The panel will open on the first valid progress update (validation passed).
                    setIsQuestionValidating(true);
                    hasLiveScoreProgressRef.current = false;
                    const precheckSelectionSnapshot = createProductSelectionSnapshot();
                    const precheckRequestId = allProductPrecheckRequestIdRef.current + 1;
                    allProductPrecheckRequestIdRef.current = precheckRequestId;
                    const shouldApplyPrecheckResponse = (): boolean => {
                        const latestSelectionSnapshot = currentProductSelectionSnapshotRef.current;
                        return (
                            allProductPrecheckRequestIdRef.current === precheckRequestId &&
                            isAllSelectionSnapshot(latestSelectionSnapshot) &&
                            areSelectionSnapshotsEqual(latestSelectionSnapshot, precheckSelectionSnapshot) &&
                            validationOwnerSessionIdRef.current === precheckSessionId &&
                            normalizeSessionIdentity(store.getState().chatComponent.currentSessionId) === precheckSessionId
                        );
                    };

                    if (onAllProductPrecheck) {
                        try {
                            let hasShownPanel = false;
                            const precheckResultRaw = await onAllProductPrecheck(sendRequest, progressUpdate => {
                                if (!shouldApplyPrecheckResponse()) {
                                    return;
                                }

                                // First valid progress update means validation passed — transition from
                                // progress bar to suggestion panel now.
                                if (!hasShownPanel) {
                                    hasShownPanel = true;
                                    setIsQuestionValidating(false);
                                    setAllProductValidationStage("loading");
                                }

                                if (Array.isArray(progressUpdate?.matchedProducts)) {
                                    setAllProductMatchedProducts(progressUpdate.matchedProducts);
                                }

                                if (progressUpdate.phase === "search") {
                                    setAllProductValidationLoadingPhase("search");
                                    setAllProductValidationMessage(allProductValidationSearchingMessage);
                                    if (Array.isArray(progressUpdate.searchCitations)) {
                                        setAllProductSearchCitations(progressUpdate.searchCitations);
                                    }
                                }

                                if (progressUpdate.phase === "score") {
                                    hasLiveScoreProgressRef.current = true;
                                    setAllProductValidationLoadingPhase("score");
                                    setAllProductValidationMessage(allProductValidationScoringMessage);
                                    setAllProductValidationStage("loading");
                                    if (Array.isArray(progressUpdate.scoreCandidates)) {
                                        setAllProductScoreCandidates(progressUpdate.scoreCandidates);
                                    }
                                }
                            });
                            const precheckResult = normalizeAllProductPrecheckResult(precheckResultRaw);
                            // Validation complete — clear the input progress bar regardless of outcome.
                            setIsQuestionValidating(false);
                            if (!shouldApplyPrecheckResponse()) {
                                return;
                            }
                            const matchedProducts = precheckResult.matchedProducts;
                            const backendSelectionDecision = precheckResult.selectionDecision ?? null;
                            // Backend returned a question_validation rejection — reset to idle so the
                            // externally-shown clarification dialog can handle the user interaction.
                            if (precheckResult.questionValidation?.is_vague) {
                                resetAllProductValidationState();
                                return;
                            }

                            // If no progress update fired (e.g. very fast response or cached result),
                            // open the suggestion panel now.
                            if (!hasShownPanel) {
                                hasShownPanel = true;
                                setAllProductValidationStage("loading");
                            }

                            if (matchedProducts.length > 0) {
                                setAllProductMatchedProducts(matchedProducts);
                                if (!hasLiveScoreProgressRef.current) {
                                    setAllProductScoreCandidates(buildScoreCandidatesFromMatchedProducts(matchedProducts));
                                    setAllProductValidationMessage(allProductValidationScoringMessage);
                                    setAllProductValidationLoadingPhase("score");
                                    setAllProductValidationStage("loading");
                                }

                                // 1. Try backend auto_select
                                const backendAutoSelectedProduct = resolvePrecheckAutoSelectedProduct(matchedProducts, backendSelectionDecision);
                                if (backendSelectionDecision?.action === "auto_select" && backendAutoSelectedProduct && onAllProductTagSelected) {
                                    const selectedProductName = backendAutoSelectedProduct.displayText ?? backendAutoSelectedProduct.productText;
                                    const selectedConfidenceValue =
                                        normalizeConfidencePercent(
                                            Number.isFinite(Number(backendAutoSelectedProduct.confidence))
                                                ? Number(backendAutoSelectedProduct.confidence)
                                                : null
                                        ) ?? 0;
                                    setAllProductValidationMessage(
                                        i18next.t("questionInput.allProductValidation.autoSelectingMessage", {
                                            product: selectedProductName,
                                            confidence: selectedConfidenceValue
                                        })
                                    );
                                    setAllProductValidationLoadingPhase("apply");
                                    setAllProductValidationStage("loading");
                                    await new Promise(resolve => setTimeout(resolve, autoSelectionPhaseHoldMs));
                                    if (!shouldApplyPrecheckResponse()) {
                                        return;
                                    }
                                    const backendAutoSelectionMeta = buildBackendAutoSelectionMeta(
                                        backendAutoSelectedProduct,
                                        matchedProducts,
                                        backendSelectionDecision
                                    );
                                    const autoSelectionApplied = await applyMatchedProductSelection(
                                        backendAutoSelectedProduct,
                                        sendRequest,
                                        backendAutoSelectionMeta
                                    );
                                    if (autoSelectionApplied) {
                                        return;
                                    }
                                    if (!shouldApplyPrecheckResponse()) {
                                        return;
                                    }
                                }

                                if (backendSelectionDecision?.action === "auto_select") {
                                    setAllProductSkipReasonCode("backend_selection_unresolved");
                                    setAllProductValidationLoadingPhase(null);
                                    setAllProductValidationMessage(backendSelectionDecision.reason?.trim() || allProductValidationMatchedMessage);
                                    setAllProductValidationStage("select");
                                    return;
                                }

                                // 2. Backend said manual_select → skip straight to selection UI
                                if (backendSelectionDecision?.action === "manual_select") {
                                    const backendManualSelectionMessage = backendSelectionDecision.reason?.trim() || allProductValidationMatchedMessage;
                                    setAllProductSkipReasonCode(backendSelectionDecision.reason_code ?? "model_unsure");
                                    setAllProductValidationLoadingPhase(null);
                                    setAllProductValidationMessage(backendManualSelectionMessage);
                                    setAllProductValidationStage("select");
                                    return;
                                }

                                // 3. No backend decision → try frontend heuristic fallback (if enabled)
                                const disableFrontendAutoSelectFallback = appInfo?.disable_frontend_autoselect_fallback === true;
                                if (!disableFrontendAutoSelectFallback) {
                                    const autoSelectionEvaluation = evaluateAutoSelection(matchedProducts);
                                    const autoSelectedProduct = autoSelectionEvaluation.selectedProduct;
                                    if (autoSelectedProduct && onAllProductTagSelected) {
                                        const selectedProductName = autoSelectedProduct.displayText ?? autoSelectedProduct.productText;
                                        const selectedConfidenceValue = Math.round(
                                            Number.isFinite(Number(autoSelectedProduct.confidence)) ? Number(autoSelectedProduct.confidence) : 0
                                        );
                                        setAllProductValidationMessage(
                                            i18next.t("questionInput.allProductValidation.autoSelectingMessage", {
                                                product: selectedProductName,
                                                confidence: selectedConfidenceValue
                                            })
                                        );
                                        setAllProductValidationLoadingPhase("apply");
                                        setAllProductValidationStage("loading");
                                        await new Promise(resolve => setTimeout(resolve, autoSelectionPhaseHoldMs));
                                        if (!shouldApplyPrecheckResponse()) {
                                            return;
                                        }
                                        const autoSelectionMeta = buildAutoSelectionMeta(autoSelectionEvaluation, matchedProducts);
                                        const autoSelectionApplied = await applyMatchedProductSelection(autoSelectedProduct, sendRequest, autoSelectionMeta);
                                        if (autoSelectionApplied) {
                                            return;
                                        }
                                        if (!shouldApplyPrecheckResponse()) {
                                            return;
                                        }
                                    }
                                    const autoSelectionSkippedMessage = buildAutoSelectionSkippedMessage(autoSelectionEvaluation);
                                    setAllProductSkipReasonCode(autoSelectionEvaluation.reason ?? null);
                                    setAllProductValidationLoadingPhase("skip");
                                    setAllProductValidationMessage(autoSelectionSkippedMessage ?? allProductValidationMatchedMessage);
                                    setAllProductValidationStage("loading");
                                    setAllProductValidationLoadingPhase(null);
                                    setAllProductValidationStage("select");
                                } else {
                                    // Frontend fallback disabled → go straight to manual selection
                                    setAllProductValidationLoadingPhase(null);
                                    setAllProductValidationMessage(allProductValidationMatchedMessage);
                                    setAllProductValidationStage("select");
                                }
                            } else {
                                setAllProductMatchedProducts([]);
                                setAllProductSearchCitations([]);
                                setAllProductScoreCandidates([]);
                                setAllProductValidationLoadingPhase(null);
                                setAllProductValidationMessage(allProductValidationFallbackConfirmMessage);
                                setAllProductValidationStage("confirm");
                            }
                        } catch (error) {
                            setIsQuestionValidating(false);
                            if (!shouldApplyPrecheckResponse()) {
                                return;
                            }
                            console.error("Error during ALL precheck flow:", error);
                            setAllProductMatchedProducts([]);
                            setAllProductSearchCitations([]);
                            setAllProductScoreCandidates([]);
                            setAllProductValidationLoadingPhase(null);
                            setAllProductValidationMessage(allProductValidationFallbackConfirmMessage);
                            setAllProductValidationStage("confirm");
                        }
                    } else {
                        setIsQuestionValidating(false);
                        if (!shouldApplyPrecheckResponse()) {
                            return;
                        }
                        setAllProductValidationLoadingPhase(null);
                        setAllProductValidationMessage(allProductValidationConfirmMessage);
                        setAllProductValidationStage("confirm");
                    }
                    return;
                }

                executeSendWithRequest(sendRequest);
            };

            const handleContinueWithAll = () => {
                if (isAllProductChoiceLocked) {
                    return;
                }
                if (!pendingSendRequest) {
                    resetAllProductValidationState();
                    return;
                }
                setIsAllProductChoiceLocked(true);
                const sendRequest = pendingSendRequest;
                resetAllProductValidationState();
                executeSendWithRequest(sendRequest);
            };

            const handleMatchedProductSelection = async (selectedProductOption: AllProductMatchedProduct) => {
                if (isAllProductChoiceLocked || !pendingSendRequest) {
                    return;
                }
                if (!onAllProductTagSelected) {
                    handleContinueWithAll();
                    return;
                }

                const sendRequest = pendingSendRequest;
                await applyMatchedProductSelection(selectedProductOption, sendRequest, {
                    source: "manual",
                    autoSelectionSnapshot: null
                });
            };

            const handleProductGroupLabelClick = (productGroup: {
                productText: string;
                bareItems: AllProductMatchedProduct[];
                versionGroups: Map<string, { label: string; items: AllProductMatchedProduct[] }>;
            }) => {
                const firstItem = productGroup.bareItems[0] ?? [...productGroup.versionGroups.values()][0]?.items[0];
                if (!firstItem) return;
                const totalCount = [...productGroup.bareItems, ...[...productGroup.versionGroups.values()].flatMap(vg => vg.items)].reduce(
                    (sum, item) => sum + item.count,
                    0
                );
                const syntheticItem: AllProductMatchedProduct = {
                    productText: firstItem.productText,
                    manual_key: firstItem.manual_key,
                    faq_key: firstItem.faq_key,
                    count: totalCount,
                    displayText: firstItem.productText
                };
                handleMatchedProductSelection(syntheticItem);
            };

            const handleVersionGroupLabelClick = (firstItemInVersion: AllProductMatchedProduct, versionItems: AllProductMatchedProduct[]) => {
                const totalCount = versionItems.reduce((sum, item) => sum + item.count, 0);
                const syntheticItem: AllProductMatchedProduct = {
                    productText: firstItemInVersion.productText,
                    manual_key: firstItemInVersion.manual_key,
                    faq_key: firstItemInVersion.faq_key,
                    count: totalCount,
                    displayText: firstItemInVersion.displayText?.split(" > ").slice(0, 2).join(" > ") ?? firstItemInVersion.productText,
                    productVersion: firstItemInVersion.productVersion
                };
                handleMatchedProductSelection(syntheticItem);
            };

            const handleCancelAllProductValidation = () => {
                if (isAllProductChoiceLocked) {
                    return;
                }
                const retainedQuestion = pendingSendRequest?.rawQuestionText ?? getCurrentQuestionInputValue();
                resetAllProductValidationState();
                setQuestion(retainedQuestion);
                setDefaultContentState(retainedQuestion);
                setIsEdited(!!retainedQuestion.trim());
                if (mentionsInputRef.current) {
                    mentionsInputRef.current.focus();
                    const retainedQuestionLength = retainedQuestion.length;
                    mentionsInputRef.current.setSelectionRange(retainedQuestionLength, retainedQuestionLength);
                }
            };

            const getAppTypeBasedOnSelectedValueOnProductFilter = (): AppType => {
                // default manual apptype to load products.
                const defaultManualProducts: Array<string> = appInfo?.default_manual_products ?? [];
                const appTypeValue =
                    defaultManualProducts.includes(productIndex?.faq_key) || defaultManualProducts.includes(productIndex?.manual_key)
                        ? selectedAppType === "ALL"
                            ? "ALL"
                            : "Manuals"
                        : selectedAppType;
                return appTypeValue as AppType;
            };

            const getAppTypeBasedOnUser = (appType: string): AppType => {
                let appTypeBasedOnLastAnswer = appType;
                const answersData = answers as
                    | [user: QuestionDetails, response: { FAQ?: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }][]
                    | undefined;
                if (answersData && answersData.length > 0) {
                    appTypeBasedOnLastAnswer = getAppTypeBasedOnLastAnswerIsRelatedToAriel(answersData);
                }
                const appTypeBasedOnSelectedProduct = getAppTypeBasedOnSelectedValueOnProductFilter();
                const appTypeBasedOnUser = (() => {
                    // If appType is not "ALL", return it as is
                    if (appType !== "ALL") {
                        return appType;
                    }

                    // Handle "ALL" appType cases
                    const shouldUseAllTab = appInfo?.use_all_tab === true;

                    if (shouldUseAllTab) {
                        return "ALL";
                    }

                    // Check if request was triggered from QuestionInput
                    const isQuestionInputTrigger = store.getState().questionInput.requestTriggerType === "QuestionInput";

                    if (isQuestionInputTrigger) {
                        // If we have answers data, use last answer-based logic
                        if (answersData && answersData.length > 0) {
                            return appTypeBasedOnLastAnswer === "ALL" ? "FAQ" : appTypeBasedOnLastAnswer;
                        }
                        // Otherwise, use selected product-based logic
                        return appTypeBasedOnSelectedProduct === "ALL" ? "FAQ" : appTypeBasedOnSelectedProduct;
                    }

                    // Default fallback for "ALL" appType
                    return "FAQ";
                })();
                if ((userInfo.isWap === "False" || userInfo.isWap === "false") && appInfo?.use_all_tab === true) {
                    // If the user is not on WAP and the appInfo indicates to use the ALL tab, we default to ALL.
                    return "ALL" as AppType;
                }
                return appTypeBasedOnUser as AppType;
            };

            const onHandleKeyDown = (event: React.KeyboardEvent<Element>) => {
                if (event.key === "Enter" && !event.shiftKey) {
                    if (showAllProductValidationUI || sendQuestionDisabled || fileUploadOrDeleteAPITriggerActive.triggerActive) {
                        if (showAllProductValidationUI) {
                            event.preventDefault();
                        }
                        return;
                    }
                    event.preventDefault();
                    sendQuestion();
                }
                if (event.ctrlKey || event.metaKey) {
                    // Ctrl+Z -> Undo
                    if (event.key === "z") {
                        if (showAllProductValidationUI) {
                            resetAllProductValidationState();
                        }
                        dispatch(QuestionInputReducerAction.updateFocusPositionToRetain(null));
                        event.preventDefault();
                        dispatch(QuestionInputReducerAction.undoEventForQuestionInput());
                        const previousState = undoStack[undoStack.length - 2];
                        if (previousState !== null && previousState !== undefined) {
                            setQuestion(previousState);
                            setDefaultContentState(previousState);
                            if (templateAppliedFlagValue) {
                                setChangingTemplateContent?.(true);
                            }
                            setIsEdited(true);
                            setChatContentCleared?.(true);
                            setClearChatTriggered?.(true);
                        }
                    }

                    // Ctrl+Y -> Redo
                    if (event.key === "y") {
                        if (showAllProductValidationUI) {
                            resetAllProductValidationState();
                        }
                        dispatch(QuestionInputReducerAction.updateFocusPositionToRetain(null));
                        event.preventDefault();
                        dispatch(QuestionInputReducerAction.redoEventForQuestionInput());
                        const redoState = redoStack[0];
                        if (redoState !== null && redoState !== undefined) {
                            setQuestion(redoState);
                            setDefaultContentState(redoState);
                            if (templateAppliedFlagValue) {
                                setChangingTemplateContent?.(true);
                            }
                            setIsEdited(true);
                            setChatContentCleared?.(true);
                            setClearChatTriggered?.(true);
                        }
                    }

                    // Ctrl+X -> Cut
                    if (event.key === "x" && mentionsInputRef?.current) {
                        if (showAllProductValidationUI) {
                            resetAllProductValidationState();
                        }
                        dispatch(QuestionInputReducerAction.updateFocusPositionToRetain(null));
                        event.preventDefault();
                        const selectedText = window.getSelection()?.toString();
                        const inputTextValue = (
                            !!(defaultContentState && defaultContentState !== "")
                                ? defaultContentState
                                : isEdited
                                  ? question
                                  : getTemplatePromptTextValue(templateContent)
                        ).trim();

                        if (!selectedText || selectedText.trim() === "") {
                            return;
                        }
                        navigator.clipboard.writeText(selectedText);
                        if (selectedText?.trim() === inputTextValue?.trim()) {
                            const newContent = "";
                            dispatch(QuestionInputReducerAction.addToQuestionInputUndoHistoryStack(newContent));
                            setQuestion(newContent);
                            setDefaultContentState(newContent);
                            if (templateAppliedFlagValue) {
                                setChangingTemplateContent?.(true);
                            }
                            setIsEdited(true);
                            setChatContentCleared?.(true);
                            setClearChatTriggered?.(true);
                        } else {
                            const startPos: number = mentionsInputRef.current?.selectionStart;
                            const endPos: number = mentionsInputRef.current?.selectionEnd;
                            const mentionsInputValue: string = mentionsInputRef.current?.value;
                            if (startPos !== -1 && mentionsInputValue) {
                                dispatch(QuestionInputReducerAction.updateFocusPositionToRetain(startPos));
                                const newContent = mentionsInputValue.slice(0, startPos) + mentionsInputValue.slice(endPos);
                                dispatch(QuestionInputReducerAction.addToQuestionInputUndoHistoryStack(newContent));
                                setQuestion(newContent);
                                setDefaultContentState(newContent);
                                if (templateAppliedFlagValue) {
                                    setChangingTemplateContent?.(true);
                                }
                                setIsEdited(true);
                                setChatContentCleared?.(true);
                                setClearChatTriggered?.(true);
                            }
                        }
                    }

                    // Ctrl+A -> Select All
                    if (event.key === "a" && mentionsInputRef?.current) {
                        event.preventDefault();
                        const length = mentionsInputRef.current.value.length;
                        mentionsInputRef.current.setSelectionRange(0, length);
                    }
                }
            };

            const clearInput = (isParentCallBack: boolean) => {
                resetAllProductValidationState();
                setQuestion("");
                setDefaultContentState("");
                const questionContainsTemplateFlag = checkIfquestionContainsTemplate();
                if (templateAppliedFlagValue && questionContainsTemplateFlag) {
                    // template applied but all prompt loaded is cleared by user.
                    if (isParentCallBack) {
                        // indicates the content cleared using template clear btn.
                        dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                        setQuestionContainsTemplate(false);
                        setChangingTemplateContent?.(false);
                        setIsEdited(false);
                    } else {
                        setChangingTemplateContent?.(true);
                        setIsEdited(true);
                    }
                    setChatContentCleared?.(true);
                } else {
                    // empty value in question input & template not used.
                    setIsEdited(false);
                    setChatContentCleared?.(true);
                    setSaveTemplateIdInStore("");
                    setQuestionContainsTemplate(false);
                    if (!templateAppliedFlagValue && onClearTemplateContent) {
                        onClearTemplateContent();
                    }
                }
                setIsHistoryPageInitialLoading?.(false);
                setIsNewChatThreadInitialLoading?.(false);
            };

            const onQuestionChange = (newValue?: string) => {
                if (isAllProductPrecheckLoading) {
                    return;
                }
                if (clarificationBanner) {
                    onDismissClarification?.(false, true);
                }
                if (showAllProductValidationUI) {
                    resetAllProductValidationState();
                }
                if (undoStack.length > 1 || (undoStack.length === 1 && undoStack[undoStack.length - 1] !== newValue && !!(newValue && newValue !== ""))) {
                    dispatch(QuestionInputReducerAction.addToQuestionInputUndoHistoryStack(!newValue ? "" : newValue));
                }
                if (!newValue) {
                    clearInput(false);
                } else {
                    const questionContainsTemplateFlag = checkIfquestionContainsTemplate();
                    if (templateAppliedFlagValue && questionContainsTemplateFlag && setChangingTemplateContent) {
                        setChangingTemplateContent(true);
                    }
                    setQuestion(newValue);
                    setIsEdited(true);
                    setDefaultContentState("");
                    setChatContentCleared?.(true);
                    setIsHistoryPageInitialLoading?.(false);
                    setIsNewChatThreadInitialLoading?.(false);
                }
            };

            const handleMentionSelect = (_id: string | number, display: string) => {
                const previewPrompt: any = templateContents.find((content: any) => content.template[0].title.includes(display));
                const templatePromptValue = getTemplatePromptTextValue(previewPrompt.template[0]);
                setQuestion(templatePromptValue);
                handleTemplateContentsBySuggestions?.(previewPrompt.template[0], previewPrompt.id);
            };

            const renderMentionSuggestion = (entry: any, search: string, highlightedDisplay: React.ReactNode) => {
                const content: any = templateContents.find((template: any) => template.template[0].title === entry.display);
                const templateValue = getTemplatePromptTextValue(content?.template[0]);
                const previewPrompt = !!(templateValue && templateValue !== "") ? templateValue : "No preview available";
                const formattedPreviewPrompt = previewPrompt.replace(/\n/g, "<br />");
                return (
                    <LightTooltip
                        content={previewPrompt}
                        title={<span dangerouslySetInnerHTML={{ __html: formattedPreviewPrompt }} />}
                        followCursor
                        TransitionComponent={Zoom}
                        placement="right-end"
                    >
                        <div>{highlightedDisplay}</div>
                    </LightTooltip>
                );
            };

            const LightTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: theme.palette.common.white,
                    color: "rgba(0, 0, 0, 0.87)",
                    boxShadow: theme.shadows[1],
                    fontSize: 11
                }
            }));

            const checkIfquestionContainsTemplate = (): boolean => {
                let questionContainsTemplateFlag = false;
                const inputTextValue = getCurrentQuestionInputValue().trim();
                const templateValue = String(getTemplatePromptTextValue(templateContent)).trim();
                const userDefaultTemplateValue = String(getUserDefaultContentTemplatePromptText(true)).trim();
                const systemDefaultTemplateValue = String(getSystemDefaultTemplatePromptText(true)).trim();
                if (!!(inputTextValue && inputTextValue !== "")) {
                    if (templateValue) {
                        questionContainsTemplateFlag = !!(templateContent && templateContent !== "")
                            ? checkIfQuestionIsSubSetOfTemplatePrompt(templateValue, inputTextValue)
                            : false;
                    } else if (userDefaultTemplateValue) {
                        questionContainsTemplateFlag = !!(userDefaultTemplateValue && userDefaultTemplateValue !== "")
                            ? checkIfQuestionIsSubSetOfTemplatePrompt(userDefaultTemplateValue, inputTextValue)
                            : false;
                    } else {
                        const allProductDefaultTemplate = getSharedSystemDefaultTemplate("ALL", templateContents);
                        const allProductDefaultTemplatePrompt = getTemplatePromptTextValue(
                            !!(allProductDefaultTemplate && allProductDefaultTemplate !== "") ? allProductDefaultTemplate?.template[0] : ""
                        );
                        const systemTemplateValue =
                            userInfo["userFavourite"]?.[selectedProduct] && !!(systemDefaultTemplateValue && systemDefaultTemplateValue !== "")
                                ? systemDefaultTemplateValue
                                : userInfo["userFavourite"]?.["ALL"] !== null && !!(allProductDefaultTemplatePrompt && allProductDefaultTemplatePrompt !== "")
                                  ? allProductDefaultTemplatePrompt
                                  : "";
                        questionContainsTemplateFlag = !!(systemTemplateValue && systemTemplateValue !== "")
                            ? checkIfQuestionIsSubSetOfTemplatePrompt(systemTemplateValue, inputTextValue)
                            : false;
                    }
                } else {
                    // value can be empty only when the input is cleared.
                    questionContainsTemplateFlag = selectedTemplateContentId ? selectedTemplateContentId !== "None" : false;
                }
                return questionContainsTemplateFlag;
            };

            const checkIfQuestionIsSubSetOfTemplatePrompt = (templatePromptText: string, question: string): boolean => {
                const questionWords = question
                    .split(" ")
                    .map(word => word.trim())
                    .filter(word => word !== "");
                const containsWordFromQuestion = questionWords.some(word => templatePromptText.includes(word));
                return containsWordFromQuestion ? containsWordFromQuestion : selectedTemplateContentId ? selectedTemplateContentId !== "None" : false;
            };

            const getTemplatePromptTextValueOnPageLoad = (templateContentData: any, templateId: string): string => {
                if (!!(templateContentData && templateContentData !== "")) {
                    const templatePrompt = getTemplatePromptTextValue(templateContentData);
                    if (isNewChatThreadInitialLoading && !templateAppliedFlagValue) {
                        handleTemplateContentsBySuggestions?.(templateContentData, templateId);
                    }
                    return templatePrompt;
                }
                return "";
            };

            const getSystemDefaultTemplatePromptText = (questionContainsTemplateCheckMethodCall: boolean): string => {
                const filteredTemplateValue: any = getSharedSystemDefaultTemplate(selectedProduct, templateContents);
                const systemDefaultTemplateValue = !!(filteredTemplateValue && filteredTemplateValue !== "")
                    ? getTemplatePromptTextValue(filteredTemplateValue?.template[0])
                    : "";
                if (
                    isNewChatThreadInitialLoading &&
                    !!(filteredTemplateValue && filteredTemplateValue !== "") &&
                    !questionContainsTemplateCheckMethodCall &&
                    !templateAppliedFlagValue
                ) {
                    handleTemplateContentsBySuggestions?.(filteredTemplateValue?.template[0], filteredTemplateValue.id);
                }
                return String(userInfo["userFavourite"][selectedProduct] === "" ? systemDefaultTemplateValue : "").replace(/[ \t]+$/gm, "");
            };

            const getUserDefaultContentTemplatePromptText = (questionContainsTemplateCheckMethodCall: boolean): string => {
                const filteredTemplateValue: any = getSharedUserDefaultContentTemplate(selectedProduct, userInfo.userFavourite, templateContents);
                const userDefaultTemplateValue = !!(filteredTemplateValue && filteredTemplateValue !== "")
                    ? getTemplatePromptTextValue(filteredTemplateValue?.template[0])
                    : "";
                if (
                    isNewChatThreadInitialLoading &&
                    !!(filteredTemplateValue && filteredTemplateValue !== "") &&
                    !questionContainsTemplateCheckMethodCall &&
                    !templateAppliedFlagValue
                ) {
                    handleTemplateContentsBySuggestions?.(filteredTemplateValue?.template[0], filteredTemplateValue.id);
                }
                return String(userDefaultTemplateValue).replace(/[ \t]+$/gm, "");
            };

            const validateConditionsAndApplyUserTemplate = () => {
                let isUserFavouriteTemplateDeletedFlagValue: boolean = false;
                if (userInfo["userFavourite"][selectedProduct] !== "" && userInfo["userFavourite"][selectedProduct] !== "None") {
                    isUserFavouriteTemplateDeletedFlagValue = !templateContents.find(
                        (tempContent: any) => tempContent.id === userInfo["userFavourite"][selectedProduct]
                    );
                }
                return userInfo["userFavourite"][selectedProduct] !== "" && userInfo["userFavourite"][selectedProduct] !== "None"
                    ? !!(selectedTemplateContentId && selectedTemplateContentId !== "")
                        ? userInfo["userFavourite"][selectedProduct] === selectedTemplateContentId
                            ? getTemplatePromptTextValueOnPageLoad(templateContent, selectedTemplateContentId)
                            : getTemplatePromptTextValueOnPageLoad(templateContent, selectedTemplateContentId)
                        : isUserFavouriteTemplateDeletedFlagValue
                          ? getSystemDefaultTemplatePromptText(false)
                          : getUserDefaultContentTemplatePromptText(false)
                    : userInfo["userFavourite"][selectedProduct] === "None"
                      ? ""
                      : getSystemDefaultTemplatePromptText(false);
            };

            const onForceStopAIResponseGenerationRetainQuestion = (answerGenerationActiveDetails: AnswerGenerationActiveDetails) => {
                resetAllProductValidationState();
                const retainedQuestion = answerGenerationActiveDetails?.questionContentToReRenderInQuestionInput ?? "";

                setQuestion(retainedQuestion);
                setDefaultContentState(retainedQuestion);
                dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                setIsEdited(true);
                setTimeout(() => {
                    dispatch(
                        QuestionInputReducerAction.updateTemplateAppliedFlag(
                            !!(answerGenerationActiveDetails?.templateId && answerGenerationActiveDetails?.templateId !== "") &&
                                answerGenerationActiveDetails?.templateId !== "None"
                        )
                    );
                }, 500);
            };

            // ---- File handling ----
            const acceptedFileTypes = resolveAcceptedUploadFileTypes(appInfo?.file_attachment?.accepted_upload_file_types);

            // Optimized: Extract file filtering logic to avoid duplication
            const filterAcceptedFiles = (files: File[]): File[] => {
                return files.filter(file => file && isFileTypeAccepted(file, acceptedFileTypes));
            };

            const maxAttachmentLimit = Number(appInfo?.file_attachment?.attachment_max_limit ?? 10);

            const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                if (answerGenerationActive || fileUploadOrDeleteAPITriggerActive.triggerActive || isAllProductPrecheckLoading) return;
                if (e.target.files) {
                    const originalFiles = Array.from(e.target.files);
                    const acceptedFiles = filterAcceptedFiles(originalFiles);
                    validateFileTypes(originalFiles, acceptedFiles, e);
                }
                e.target.value = "";
            };
            const handleFilePaste = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (answerGenerationActive || fileUploadOrDeleteAPITriggerActive.triggerActive || isAllProductPrecheckLoading) return;
                const originalFiles = Array.from(e.clipboardData.items)
                    .filter(item => item.kind === "file")
                    .map(item => item.getAsFile())
                    .filter(Boolean) as File[];
                const acceptedFiles = filterAcceptedFiles(originalFiles);
                validateFileTypes(originalFiles, acceptedFiles, e);
            };
            const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
                if (answerGenerationActive || fileUploadOrDeleteAPITriggerActive.triggerActive || isAllProductPrecheckLoading) return;
                e.preventDefault();
                if (e.dataTransfer.files) {
                    const originalFiles = Array.from(e.dataTransfer.files);
                    const acceptedFiles = filterAcceptedFiles(originalFiles);
                    validateFileTypes(originalFiles, acceptedFiles, e);
                }
            };
            const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

            const validateFileTypes = (
                originalFiles: File[],
                acceptedFiles: File[],
                e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement> | React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
                if (originalFiles.length !== acceptedFiles.length) {
                    showSnackbar(i18next.t("fileAttachment.filesNotAccepted") + " : " + acceptedFileTypes.join(", "), "error");
                } else {
                    if (acceptedFiles.length > 0) {
                        e.preventDefault();
                        addFiles(acceptedFiles);
                        initiateFilesUpload();
                        if (clarificationBanner) {
                            onDismissClarification?.(true);
                        }
                    }
                }
            };

            const handleUploadFiles = async (attachments: Array<Attachment>) => {
                let newAttachments: Attachment[] = [];
                try {
                    let uploadedFileAttachmentIds: Array<FileAttachmentDetails> = [];
                    if (attachments && attachments.length > 0) {
                        const existingServerSideIds: Array<FileAttachmentDetails> = store.getState().questionInput.serverSideFileAttachmentsIds;
                        newAttachments = attachments.filter(
                            att => !existingServerSideIds.some((serverAtt: FileAttachmentDetails) => serverAtt.frontEndId === att.id)
                        );
                        if (newAttachments && newAttachments.length > 0) {
                            const formData = new FormData();
                            newAttachments.forEach(att => {
                                if (att.file instanceof File) {
                                    // Trim trailing dots first; check hasExt on the trimmed name so
                                    // "report.pdf..." is not treated as extension-less.
                                    // Fall back to "file" if trimming leaves an empty string.
                                    const trimmedBase = (att.file.name || "file").replace(/\.+$/, "") || "file";
                                    const lastDot = trimmedBase.lastIndexOf(".");
                                    // hasExt: dot must not be the first or last character.
                                    const hasExt = lastDot > 0 && lastDot < trimmedBase.length - 1;
                                    const extFromMime = hasExt
                                        ? ""
                                        : (Object.entries(EXTENSION_TO_MIME_MAP)
                                              .filter(([ext]) => acceptedFileTypes.includes(ext))
                                              .find(([, mimes]) => (mimes as string[]).includes(att.file.type))?.[0] ?? "");
                                    formData.append("file", att.file, trimmedBase + extFromMime + "**" + att.id);
                                }
                            });
                            const fileUploadResponse = await UploadFile(formData);
                            if (!fileUploadResponse?.ok) {
                                const result = await fileUploadResponse?.json();
                                if (result?.error) {
                                    console.error("Template fetch error:", result.error);
                                    showSnackbar(result.error, "error");
                                    handleAutoFileDettachmentOnError(newAttachments, result.error);
                                }
                            } else {
                                const result = await fileUploadResponse.json();
                                const returnedFiles: Array<FileAttachmentDetails> = result?.files ?? [];
                                uploadedFileAttachmentIds = [...existingServerSideIds, ...returnedFiles];
                                dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds(uploadedFileAttachmentIds));
                                dispatch(QuestionInputReducerAction.updateFileUploadOrDeleteAPITriggerActive({ triggerActive: false, triggerAPI: null }));
                                // Reconcile: auto-detach any file the server silently omitted from a partial-success response
                                const returnedFrontEndIds = new Set(returnedFiles.map(f => f.frontEndId));
                                const missingAttachments = newAttachments.filter(att => !returnedFrontEndIds.has(att.id));
                                if (missingAttachments.length > 0) {
                                    handleAutoFileDettachmentOnError(missingAttachments, i18next.t("fileAttachment.uploadPartialFailure"));
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error("File upload failed:", error);
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    showSnackbar(errorMessage, "error");
                    handleAutoFileDettachmentOnError(newAttachments, error);
                }
            };

            const handleAutoFileDettachmentOnError = (newAttachments: Attachment[], error: any) => {
                dispatch(
                    QuestionInputReducerAction.updateFileUploadOrDeleteAPITriggerActive({
                        triggerActive: true,
                        triggerAPI: TriggerTypeAPI.FILE_UPLOAD_DETACH,
                        error: error
                    })
                );
                setTimeout(() => {
                    removeAttachment(
                        newAttachments.map(att => att.id),
                        TriggerTypeAPI.FILE_UPLOAD_DETACH
                    );
                }, 2000);
            };

            const initiateFilesUpload = () => {
                setTimeout(() => {
                    const attachments: Array<Attachment> = store.getState().questionInput.fileAttachments;
                    handleUploadFiles(attachments);
                }, 1000);
            };

            const handleJoyrideCallbackForFileAttachment = (data: any) => {
                const { status } = data;
                const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

                if (finishedStatuses.includes(status)) {
                    endTourFileAttachment();
                }
            };

            const endTourFileAttachment = useCallback(() => {
                setTourFileAttachment(prevState => ({
                    ...prevState,
                    run: false
                }));
                if (userInfo.fileAttachmentTourDone === "false" || !userInfo.fileAttachmentTourDone) {
                    saveUserSettings({
                        chatTourDone: "true",
                        templateTourDone: "true",
                        fileAttachmentTourDone: "true",
                        answerTourDone: "false",
                        answerTabTourDone: "false"
                    });
                }
                userInfo.fileAttachmentTourDone = "true";
                setActiveFileAttachmentTour?.(false);
            }, []);

            return (
                <>
                    <Joyride
                        continuous
                        steps={tourFileAttachment.steps}
                        callback={data => handleJoyrideCallbackForFileAttachment(data)}
                        run={tourFileAttachment.run}
                        hideCloseButton
                        scrollToFirstStep
                        showSkipButton
                        disableCloseOnEsc
                        disableOverlayClose
                        disableScrolling
                        locale={{
                            next: i18next.t(`appTour.next`),
                            skip: i18next.t(`appTour.skip`),
                            back: i18next.t(`appTour.back`),
                            last: i18next.t(`appTour.last`)
                        }}
                        spotlightPadding={1}
                        styles={{
                            options: {
                                zIndex: 1000000,
                                width: window.innerWidth < 1000 ? "200px" : "380px"
                            },
                            tooltip: {
                                padding: window.innerWidth < 1000 ? "10px" : "15px",
                                fontSize: window.innerWidth < 1000 ? "10px" : "16px"
                            },
                            tooltipContent: {
                                fontSize: window.innerWidth < 1000 ? "10px" : "16px"
                            }
                        }}
                    />
                    <FileAttachmentPreviewRow onRemove={removeAttachment} answerGenerationActive={answerGenerationActive} />
                    {!answerGenerationActive && fileAttachments.length > 0 && <Divider sx={{ width: "97%", margin: "4px auto 0px auto" }} />}
                    <Box sx={{ display: "flex", justifyContent: "center", margin: "2px auto" }}>
                        {fileUploadOrDeleteAPITriggerActive.triggerActive && <FileUploadOrDeleteProgressBar />}
                    </Box>
                    <Typography variant="subtitle2" align="center" sx={{ color: "text.secondary", fontStyle: "italic", padding: "0px" }}>
                        {fileUploadOrDeleteAPITriggerActive.triggerAPI
                            ? fileUploadOrDeleteAPITriggerActive.triggerAPI === TriggerTypeAPI.FILE_UPLOAD_DETACH
                                ? i18next.t("chat.detachingFiles") + " : " + ` ${fileUploadOrDeleteAPITriggerActive.error}`
                                : fileUploadOrDeleteAPITriggerActive.triggerAPI === TriggerTypeAPI.FILE_UPLOAD
                                  ? i18next.t("chat.uploadingFiles")
                                  : i18next.t("chat.deletingFiles")
                            : null}
                    </Typography>
                    <div style={{ width: "100%" }} onDrop={handleDrop} onDragOver={handleDragOver}>
                        <div style={{ width: "100%" }}>
                            <Stack
                                horizontal={false}
                                data-testid="question-input-surface"
                                style={{
                                    width: "100%",
                                    background: questionInputSurfaceBackground,
                                    border: questionInputSurfaceBorder,
                                    borderRadius: "10px",
                                    padding: "0px"
                                }}
                            >
                                <div className={styles.questionInputShell}>
                                    {clarificationBanner &&
                                        (() => {
                                            const clarificationTypeConfig = {
                                                greeting: {
                                                    titleKey: "questionInput.clarificationDialog.greetingTitle",
                                                    reasonKey: "questionInput.clarificationDialog.greetingReason",
                                                    accentColor: "#0078d4",
                                                    Icon: SentimentSatisfiedAltIcon
                                                },
                                                vague: {
                                                    titleKey: "questionInput.clarificationDialog.vagueTitle",
                                                    reasonKey: "questionInput.clarificationDialog.vagueReason",
                                                    accentColor: "#8764b8",
                                                    Icon: HelpOutlineIcon
                                                },
                                                off_topic: {
                                                    titleKey: "questionInput.clarificationDialog.offTopicTitle",
                                                    reasonKey: "questionInput.clarificationDialog.offTopicReason",
                                                    accentColor: "#c55a11",
                                                    Icon: ReportProblemOutlinedIcon
                                                }
                                            } as const;
                                            const cfg =
                                                clarificationTypeConfig[clarificationBanner.type as keyof typeof clarificationTypeConfig] ??
                                                clarificationTypeConfig.vague;
                                            const { Icon, accentColor, titleKey, reasonKey } = cfg;
                                            return (
                                                <Box
                                                    sx={{
                                                        borderRadius: "10px 10px 0 0",
                                                        borderBottom: `1px solid ${accentColor}20`,
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "8px",
                                                            padding: "10px 12px",
                                                            borderLeft: `3px solid ${accentColor}`
                                                        }}
                                                    >
                                                        <Icon sx={{ fontSize: "18px", color: accentColor, flexShrink: 0, mt: "2px" }} />
                                                        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "13px",
                                                                    fontWeight: 600,
                                                                    color: "#1b1b1b",
                                                                    lineHeight: 1.3,
                                                                    mb: "2px",
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis"
                                                                }}
                                                            >
                                                                {i18next.t(titleKey)}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "12px",
                                                                    color: "#444",
                                                                    lineHeight: 1.5,
                                                                    display: "-webkit-box",
                                                                    WebkitLineClamp: 1,
                                                                    WebkitBoxOrient: "vertical",
                                                                    overflow: "hidden"
                                                                }}
                                                            >
                                                                {i18next.t(reasonKey)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                                                            {onSendAnyway && (
                                                                <Button
                                                                    size="small"
                                                                    onClick={onSendAnyway}
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "4px 10px",
                                                                        height: "28px",
                                                                        border: `1px solid ${accentColor}`,
                                                                        color: accentColor,
                                                                        background: "white",
                                                                        borderRadius: "6px",
                                                                        cursor: "pointer",
                                                                        whiteSpace: "nowrap"
                                                                    }}
                                                                >
                                                                    {i18next.t("questionInput.clarificationDialog.sendAnywayButton")}
                                                                </Button>
                                                            )}
                                                            {onDismissClarification && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => onDismissClarification?.()}
                                                                    title={i18next.t("questionInput.clarificationDialog.dismissButton")}
                                                                    sx={{ color: "#888", padding: "2px" }}
                                                                >
                                                                    <CloseIcon sx={{ fontSize: "16px" }} />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })()}
                                    <div className={styles.questionInputBody} data-testid="question-input-content">
                                        <MentionsInput
                                            inputRef={mentionsInputRef}
                                            id="question-input"
                                            className={`${styles.questionInputTextArea} ${clarificationBanner ? styles.questionInputTextAreaClarification : ""}`.trim()}
                                            placeholder={
                                                templateAppliedFlagValue
                                                    ? i18next.t("chat.templateApplied")
                                                    : isQuestionInputDisabled && !isQuestionValidating
                                                      ? i18next.t("chat.no_input")
                                                      : i18next.t("chat.input")
                                            }
                                            value={getCurrentQuestionInputValue()}
                                            onChange={e => onQuestionChange(e.target.value)}
                                            onKeyDown={onHandleKeyDown}
                                            onPaste={event => {
                                                const isFileExist = Array.from(event.clipboardData.items).filter(item => item.kind === "file").length;
                                                if (isFileExist > 0) {
                                                    event.preventDefault();
                                                    handleFilePaste(event);
                                                }
                                            }}
                                            disabled={isQuestionInputDisabled}
                                            autoFocus
                                            style={
                                                {
                                                    ...MentionStyle,
                                                    width: "100%",
                                                    border: "none",
                                                    background: "transparent",
                                                    resize: "none",
                                                    paddingLeft: 0,
                                                    paddingRight: 0
                                                } as MentionsInputStyle
                                            }
                                            forceSuggestionsAboveCursor={true}
                                            allowSuggestionsAboveCursor={true}
                                        >
                                            <Mention
                                                trigger="/"
                                                markup="@[__display__](__id__)"
                                                displayTransform={(id: string, display: string) => display || id}
                                                data={formattedTemplateTitles}
                                                onAdd={handleMentionSelect}
                                                renderSuggestion={renderMentionSuggestion}
                                            />
                                        </MentionsInput>
                                    </div>
                                    {isQuestionValidating && (
                                        <Box sx={{ width: "92%", margin: "0 auto 4px" }}>
                                            <LinearProgress variant="indeterminate" sx={{ borderRadius: "2px", height: "3px" }} />
                                            <Typography
                                                variant="caption"
                                                sx={{ display: "block", textAlign: "center", color: "text.secondary", mt: 0.5, fontSize: "11px" }}
                                            >
                                                {i18next.t("questionInput.allProductValidation.validatingMessage")}
                                            </Typography>
                                        </Box>
                                    )}
                                    <div className={styles.questionInputActions} data-testid="question-input-actions">
                                        <>
                                            <div className={styles.questionInputActionLeft}>
                                                <div title={i18next.t("chat.attachFile")} id="FileAttachmentBtn">
                                                    <FileAttachmentButton
                                                        inputRef={fileInputRef}
                                                        onFileInputChange={handleFileInputChange}
                                                        acceptedFileTypes={acceptedFileTypes}
                                                        disabled={
                                                            disableTextarea ||
                                                            showAllProductValidationUI ||
                                                            fileAttachments?.length === maxAttachmentLimit ||
                                                            answerGenerationActive ||
                                                            fileUploadOrDeleteAPITriggerActive.triggerActive
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            {(showAbortButton || showSendButton) && (
                                                <div className={styles.questionInputActionRight}>
                                                    {showAbortButton ? (
                                                        <ChatAPIResponseGenerationAbortButton />
                                                    ) : (
                                                        <LightTooltip title={i18next.t("questionInput.tooltip")} placement="top">
                                                            <Button
                                                                id="sendButton"
                                                                size="large"
                                                                icon={
                                                                    <ArrowCircleRightIcon
                                                                        sx={{
                                                                            color: "white",
                                                                            backgroundColor: "#6a6ee6",
                                                                            borderRadius: "50%",
                                                                            width: isCitationActive ? "38px" : "48px",
                                                                            height: isCitationActive ? "38px" : "48px"
                                                                        }}
                                                                    />
                                                                }
                                                                disabled={sendQuestionDisabled}
                                                                onClick={() => sendQuestion()}
                                                                style={{
                                                                    height: isCitationActive ? "46px" : "54px",
                                                                    width: isCitationActive ? "46px" : "54px",
                                                                    minWidth: isCitationActive ? "46px" : "54px",
                                                                    padding: 0
                                                                }}
                                                            />
                                                        </LightTooltip>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    </div>
                                </div>
                            </Stack>
                        </div>
                        {SnackbarComponent}
                    </div>
                    {shouldShowInlineValidationUI && (
                        <div data-testid="all-product-validation-area">
                            {showAllProductValidationLoader && <div data-testid="all-product-validation-loader" />}
                            {(showAllProductValidationSelection || allProductValidationStage === "confirm") && (
                                <>
                                    <Button id="continueWithAllButton" disabled={isAllProductChoiceLocked} onClick={handleContinueWithAll}>
                                        {allProductValidationContinueLabel}
                                    </Button>
                                    <Button id="cancelAllValidationButton" disabled={isAllProductChoiceLocked} onClick={handleCancelAllProductValidation}>
                                        {allProductValidationCancelLabel}
                                    </Button>
                                    {showAllProductValidationSelection &&
                                        allProductMatchedProducts.map(product => (
                                            <button
                                                key={`${product.manual_key}-${product.faq_key}`}
                                                onClick={() => handleMatchedProductSelection(product)}
                                                disabled={isAllProductChoiceLocked}
                                            >
                                                {product.productText}
                                                {product.count}
                                            </button>
                                        ))}
                                </>
                            )}
                        </div>
                    )}
                </>
            );
        }
    )
);

QuestionInput.displayName = "QuestionInput";
